/* eslint-disable no-shadow */
/* eslint-disable no-undef */
import { useWorkflowCredits } from 'hooks/useWorkflowCredits';
import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';
import { iFollowupItem } from 'interfaces/iFollowupItem';
import { useSnackbar } from 'notistack';
import { GlobalContext } from 'providers/GlobalProvider';
import CreditsAPI from 'api/CreditsAPI';
import { clearWorkflowRunId, getWorkflowRunId } from 'utils/workflowRunId';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  Prompt,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';
import format from 'date-fns/format';
import axios from 'axios';
import KeycloakService from 'services/KeycloakService';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import ClassifyResultsComp from './ClassifyResultsComp';
import ImportWorkflowScreening from 'pages/screening/ImportWorkflow';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import CasesAPI from 'api/CasesAPI';
import { useWorkflowAttachments } from 'hooks/useWorkflowAttachments';
import {
  attachmentTurnConfig,
  attachmentTurnFields,
  makeClassifyAttachmentAdapter,
} from 'components/TradeHelper/classifyAttachmentAdapter';
import {
  attachmentHint,
  attachmentOnlyMessage,
  attachmentPreparationNote,
  attachmentSummary,
} from 'components/TradeHelper/classifyAttachments';
import { useAttachmentLimits } from 'components/TradeHelper/classifyAttachmentLimits';
import type { ClassifyAttachmentRef } from 'components/TradeHelper/classifyUpload';
import { classifyAuthHeaders } from 'components/TradeHelper/classifyUpload';
import { loadDraft, registerRecoverable } from 'services/sessionRecovery';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Modal,
  Tooltip,
} from '@mui/material';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import NewTariffRagSearch from 'components/TariffChecker/RagAgent/NewTariffRagSearch';
import TariffPage from './MergeTariff';
import ReadOnlyTariffMirror from './ReadOnlyTariffMirror';
import CustomsFlagsSummary, {
  CustomsFlagsDetails,
} from 'components/Screening/CustomsFlagsSummary';
import RequiredLicense from 'pages/assign/RequiredLicense';
import TrialRunUsedPanel from 'components/Workflows/TrialRunUsedPanel';
import { LicenseType } from 'pages/assign/LicenseBadge';
import {
  WorkflowAssistantHeader,
  WorkflowAttachmentChips,
  WorkflowComposer,
  WorkflowComposerAttachments,
  WorkflowNotice,
  WorkflowCreditWarning,
  WorkflowPageHeader,
  WorkflowProgressHeader,
  WorkflowProgressStep,
  WorkflowProgressTimeline,
  WorkflowStreamingResponse,
  useWorkflowChatScroll,
  useScrollWorkflowIntoView,
} from 'components/Workflows/StandardWorkflowLayout';
import type {
  CrossRulingLink,
  HtsUiPatch,
} from 'components/TradeHelper/HtsClassificationChat';
import {
  buildPatchDisplayText,
  formatThoughtDuration,
  toErrorMessage,
} from 'components/TradeHelper/HtsClassificationChat';
// Only for the leaf typography/thought-step/thinking-card classes below (all
// self-contained — none depend on .bite-chatkit-shell/.bite-hts-react-chat's
// height/overflow ancestor chain, which is what caused the earlier layout
// bugs when the full chat widget was embedded here).
import 'resources/styles/chatkit-classify.css';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;
interface IMessage {
  id: number | string;
  sender: 'user' | 'bot';
  text: string | JSX.Element;
  type?: string;
  payload?: any;
  options?: string[];
}

const IMPORT_CHAT_RECOVERY_KEY = 'workflow-import-chat';

// A message's `text` can in principle be a JSX.Element, which can't survive a
// JSON round-trip (and wouldn't mean anything after a fresh page load even if
// it did) -- drop any that aren't plain strings rather than let JSON.stringify
// throw or silently corrupt the draft. `shouldStream: true` on a captured
// message would resurrect a bot reply frozen mid-type after restore, with no
// live stream left to finish it -- force it false so it renders as done.
interface ImportChatRecoverableState {
  messages: IMessage[];
  input: string;
  currentStage: number;
  userProductDesc: string;
  userProductDescFix: string;
  userECCN: string;
  cmdtDesc: string | null;
  HTSCode: string | null;
  isHtsResultConfirmed: boolean;
  inputHTS: string;
  explanatoryNotes: string | null;
  sectionNotes: string | null;
  crossLinks: CrossRulingLink[];
  originCntry: string;
  ticketId: string;
  material: string;
  intendedUse: string;
  physicalState: string;
  followupItems: iFollowupItem[];
  newTicketName: string;
}

const sanitizeMessagesForRecovery = (messages: IMessage[]): IMessage[] =>
  messages
    .filter((message) => typeof message.text === 'string')
    .map((message) =>
      message.payload
        ? { ...message, payload: { ...message.payload, shouldStream: false } }
        : message,
    );

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
};

const normalizeWorkflowChoice = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^option\s+/, '')
    .replace(/[.):]+$/, '')
    .replace(/\s+/g, ' ');

const resolveWorkflowChoice = (value: string, options: string[]) => {
  const normalizedValue = normalizeWorkflowChoice(value);
  const numericChoice = /^\d+$/.test(normalizedValue)
    ? Number(normalizedValue)
    : NUMBER_WORDS[normalizedValue];

  if (numericChoice && options[numericChoice - 1]) {
    return options[numericChoice - 1];
  }

  return options.find((option) => {
    const normalizedOption = normalizeWorkflowChoice(option);
    return (
      normalizedOption === normalizedValue ||
      normalizedOption.startsWith(`${normalizedValue} `)
    );
  });
};

const getDefaultTariffInputs = (htsCode = '') => ({
  htsCode: htsCode,
  originCountry: '',
  originIso2: '',
  materialPct: 40,
  amountValue: 10,
  amountUnit: 'kg',
  loadingCountry: '',
  transportMethod: 'Ocean',
});

const cloneTariffSnapshot = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value));

const hasTariffSnapshotResults = (value: any) =>
  Array.isArray(value?.results?.summaryRows)
    ? value.results.summaryRows.length > 0
    : Array.isArray(value?.results)
    ? value.results.length > 0
    : false;

const parseSavedDate = (value: any) => {
  const parsed = value ? new Date(value) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const savedString = (value: any) =>
  typeof value === 'string'
    ? value
    : value === undefined || value === null
    ? ''
    : String(value);

const normalizeSavedTariffSnapshot = (value: any) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const nestedResults =
    value.results && typeof value.results === 'object' ? value.results : {};
  const summaryRows = Array.isArray(value.results)
    ? value.results
    : Array.isArray(nestedResults.summaryRows)
    ? nestedResults.summaryRows
    : [];
  const sourceLines = Array.isArray(value.lines)
    ? value.lines
    : Array.isArray(nestedResults.lines)
    ? nestedResults.lines
    : [];

  return {
    ...value,
    enabledExclusions:
      value.enabledExclusions || nestedResults.enabledExclusions || {},
    enabledFtas: value.enabledFtas || nestedResults.enabledFtas || {},
    optionalExclusions:
      value.optionalExclusions || nestedResults.optionalExclusions || [],
    ftaOptions: value.ftaOptions || nestedResults.ftaOptions || [],
    requiredAmountUnits:
      value.requiredAmountUnits || nestedResults.requiredAmountUnits || [],
    // Keep import-ban metadata with the flattened result. The workflow uses
    // this snapshot again for its completed/resumed tariff step, where the
    // applicability questions must remain available alongside a BARRED row.
    importBans: Array.isArray(value.importBans)
      ? value.importBans
      : Array.isArray(nestedResults.importBans)
      ? nestedResults.importBans
      : [],
    banGateAnswers:
      value.banGateAnswers || nestedResults.banGateAnswers || {},
    activeImportBan:
      value.activeImportBan !== undefined
        ? value.activeImportBan
        : nestedResults.activeImportBan ?? null,
    results: summaryRows,
    lines: sourceLines.map((line: any) => ({
      ...line,
      rows: Array.isArray(line?.rows) ? line.rows : [],
    })),
  };
};

const buildInitialUserMessageText = (
  productDescription: string,
  material: string,
  intendedUse: string,
  physicalState: string,
) => {
  const optionalFields = [
    ['Material', material],
    ['Intended Use', intendedUse],
    ['Physical State', physicalState],
  ].filter(([, value]) => value && value.trim() !== '');

  if (optionalFields.length === 0) {
    return productDescription;
  }

  const optionalFieldsText = optionalFields
    .map(([label, value]) => `- ${label}: ${value}`)
    .join('\n');

  return `${productDescription}\nOptional Details:\n${optionalFieldsText}`;
};

const mergeBoolMapsPreferTrue = (
  ...maps: Array<Record<string, boolean> | undefined | null>
) => {
  const merged: Record<string, boolean> = {};

  maps.forEach((map) => {
    if (!map || typeof map !== 'object') {
      return;
    }

    Object.entries(map).forEach(([key, value]) => {
      merged[key] = Boolean(merged[key]) || Boolean(value);
    });
  });

  return merged;
};

// Same caps HtsClassificationChatClaude applies to its own saved snapshot.
// MAX_STREAM_ITEMS bounds what we keep live (and therefore what aiStream
// persists); the length cap only applies to the htsChatHistory copy, since
// StreamDisplay JSON.parses each aiStream entry and a truncated item would
// render as "Invalid response received".
const MAX_STREAM_ITEMS = 100;
const MAX_STREAM_ITEM_LENGTH = 4000;

const isHtsPlaceholderValue = (value?: string | null) =>
  /^<[^>]+>$/.test(value?.trim() || '');

const cleanHtsValue = (value?: string | null) => {
  const trimmed = value?.trim() || '';
  return isHtsPlaceholderValue(trimmed) ? '' : trimmed;
};

const normalizeCrossLinks = (links: any[]): CrossRulingLink[] =>
  links
    .map((item) => {
      if (typeof item === 'string') {
        return {
          title: item,
          url: item,
          relevance_percent: null,
          relevance_note: null,
        };
      }

      return {
        title: item?.title ?? item?.url ?? 'Untitled ruling',
        url: item?.url ?? '',
        relevance_percent:
          typeof item?.relevance_percent === 'number'
            ? item.relevance_percent
            : null,
        relevance_note: item?.relevance_note ?? null,
      };
    })
    .filter((item) => item.url);

const isHtsClassificationComplete = (
  latest?: HtsUiPatch['latest'] | null,
): boolean =>
  Boolean(
    latest &&
      cleanHtsValue(latest.hts_code) &&
      cleanHtsValue(latest.cmdt_desc) &&
      latest.explanatory_notes &&
      latest.section_chapter_notes,
  );

// .chat-container (index.css) sets font-family: Arial, sans-serif for this
// entire chat panel — it cascades down and overrides both the app's own
// Roboto/Inter default and chatkit-classify.css's Inter-based typography.
// Applied locally on the HTS content wrappers below rather than changing the
// shared .chat-container rule, which also affects the tariff/screening bubbles.
const HTS_FONT_FAMILY = 'var(--font-family, Inter, sans-serif)';

// Mirrors HtsClassificationChatClaude's own (private, unexported) constants of
// the same name — duplicated locally rather than exporting them from the
// widget file for a one-off need. INTERIM_NOTE_STAGES render softer/italic
// (a status aside, not the answer); REPLACE_STAGES supersede the previous
// live bubble in place instead of appending a new one, so an evolving
// multi-step graph traversal updates one bubble rather than stacking dozens.
const HTS_REPLACE_STAGES = new Set([
  'CHAPTER_CONFIRMED',
  'GRAPH_PROGRESS',
  'FINAL_CLASSIFICATION',
  'FINAL_CLASSIFICATION_ACCEPTED',
  'FINAL_REVIEW',
]);

// The UiStages that carry actionable content the user is meant to respond to — a
// follow-up question with options, the proposed/final classification, or a
// terminal error/stop. ONLY these become their own chat bubble; everything else
// folds into the turn's "Thought for Ns" trail.
//
// Deliberately an allowlist of ANSWERS rather than of narration, matching
// HtsClassificationChatClaude. The backend adds interim stages over time
// (PHASE_START and FINDING are recent), and with a narration allowlist each new
// one silently defaulted to "answer" — rendering internal progress notes as chat
// bubbles and cutting the thought trail short.
const HTS_ANSWER_STAGES = new Set([
  'CHAPTER_SUGGESTED',
  'GRAPH_CLARIFICATION',
  'CLARIFICATION',
  'FINAL_REVIEW',
  'FINAL_CLASSIFICATION',
  'FINAL_CLASSIFICATION_ACCEPTED',
  'STOPPED',
  'ERROR',
]);

// Interim stages kept OUT of the trail only because each already publishes a
// terse UiStage.FINDING carrying the same determination — showing both would
// duplicate every line. Their own text is the full official tariff description
// (markdown headings plus the inline HTML the HTS text uses), which suits the
// answer message and the Classification Path panel but renders as literal
// `### HTS Number:` / `<il>10 kg</il>` in a plain-text trail row.
const HTS_TRAIL_EXCLUDED_STAGES = new Set([
  'NORMALIZED',
  'CHAPTER_CONFIRMED',
  'GRAPH_PROGRESS',
]);

// How long each queued trail line waits before surfacing. Matches
// HtsClassificationChatClaude's THOUGHT_REVEAL_MS: slow enough that the panel
// visibly fills across a long wait rather than dumping a poll's whole batch at
// once, fast enough that a short turn isn't held up (anything still queued is
// flushed as soon as the turn ends).
const HTS_TRAIL_REVEAL_MS = 700;

// One row in the turn's thought trail. `phase` is a stage transition (bold),
// `finding` a determination such as "Chapter 84 — machinery" (mid-weight), and
// `step` an activity line (secondary). All render as one continuous bulleted
// list, the way Claude's and ChatGPT's activity trails read — same model as
// HtsClassificationChatClaude's HtsThoughtStep.
type HtsTrailStep = {
  text: string;
  level: 'phase' | 'step' | 'finding';
};

// Maps a patch's UiStage onto its trail level, or null when the patch should not
// appear in the trail at all.
const htsTrailLevelForStage = (
  stage: string,
): HtsTrailStep['level'] | null => {
  if (stage === 'PHASE_START') return 'phase';
  if (stage === 'FINDING') return 'finding';
  if (HTS_TRAIL_EXCLUDED_STAGES.has(stage)) return null;
  if (HTS_ANSWER_STAGES.has(stage)) return null;
  return 'step';
};

const HTS_SUPERSCRIPT_DIGITS: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};
const HTS_SUBSCRIPT_DIGITS: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
};

// Digits-only content maps to real Unicode sub/superscript characters (renders anywhere, no
// markup needed); anything else (rare) just loses the tag rather than showing raw markup.
const convertHtsDigitScript = (
  content: string,
  map: Record<string, string>,
): string =>
  /^\d+$/.test(content)
    ? content
        .split('')
        .map((digit) => map[digit])
        .join('')
    : content;

// HTS graph descriptions use raw HTML tags for officially formatted terms — <i>/<il>
// (italics), <u> (underline), <sub>/<sup> (chemical formulas, units), <span> (inline
// styling), <newline></newline> (a literal line break). Claude quotes these verbatim into
// its responses. react-markdown escapes raw HTML by default, so without this conversion the
// tags render as literal text.
const htsHtmlEmphasisToMarkdown = (text: string): string =>
  text
    .replace(/<newline\s*\/?>\s*(?:<\/newline>)?/gi, '  \n')
    .replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, '$1')
    .replace(/<(i|il|em|u)>([\s\S]*?)<\/\1>/gi, '*$2*')
    .replace(/<(b|strong)>([\s\S]*?)<\/\1>/gi, '**$2**')
    .replace(/<sup>([\s\S]*?)<\/sup>/gi, (_match, content) =>
      convertHtsDigitScript(content, HTS_SUPERSCRIPT_DIGITS),
    )
    .replace(/<sub>([\s\S]*?)<\/sub>/gi, (_match, content) =>
      convertHtsDigitScript(content, HTS_SUBSCRIPT_DIGITS),
    );

// ReactMarkdown's default anchor rendering opens in the SAME tab, which would
// navigate the user away from an in-progress turn (e.g. the HTS.gov
// reference link on the final result). Module-scope so the reference is
// stable across renders, as react-markdown's `components` prop expects.
const HTS_MARKDOWN_COMPONENTS: Components = {
  a: ({ node: _node, ...props }) => (
    <a {...props} target='_blank' rel='noopener noreferrer' />
  ),
};

// Same token-by-token reveal WorkflowStreamingResponse does (3 tokens per 18ms
// tick, matching HtsClassificationChatClaude's own timing), but re-rendering the
// growing substring through ReactMarkdown each tick instead of as plain text —
// WorkflowStreamingResponse itself isn't touched since it's shared by every
// other bot-message type in this file.
const HtsMarkdownMessage: React.FC<{
  text: string;
  animate: boolean;
}> = ({ text, animate }) => {
  const [visibleText, setVisibleText] = useState(animate ? '' : text);
  // Once this bubble (a stable message id reused across finalize/replace calls —
  // e.g. a question later getting a thought-summary attached) has fully streamed
  // in, later text changes just snap in directly instead of replaying the
  // typewriter — matching HtsClassificationChatClaude, which also streams a
  // bubble in once and snaps every later update to it.
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (!animate || hasAnimatedRef.current) {
      setVisibleText(text);
      return undefined;
    }

    setVisibleText('');
    const tokens = text.match(/\S+|\s+/g) || [text];
    let tokenIndex = 0;
    const intervalId = window.setInterval(() => {
      tokenIndex = Math.min(tokenIndex + 3, tokens.length);
      setVisibleText(tokens.slice(0, tokenIndex).join(''));

      if (tokenIndex >= tokens.length) {
        window.clearInterval(intervalId);
        hasAnimatedRef.current = true;
      }
    }, 18);

    return () => window.clearInterval(intervalId);
  }, [animate, text]);

  // Deliberately no scroll nudge here: this bubble's tick-by-tick reveal
  // must not pin the view to the bottom while it types, so someone reading
  // an earlier message isn't dragged back down mid-reveal.

  return (
    <ReactMarkdown components={HTS_MARKDOWN_COMPONENTS}>
      {htsHtmlEmphasisToMarkdown(visibleText)}
    </ReactMarkdown>
  );
};

// Matches HtsClassificationChatClaude's per-message "Thought for Xs >"
// summary — reusing its exported formatThoughtDuration and the same
// chatkit-classify.css classes so it looks identical — except always
// expanded (no click-to-toggle) to match EARChatUI's "Thought for Ns" panel,
// which is always shown open rather than collapsed by default. `steps` here
// are ChatUI.tsx's own accumulated REAL interim-note narration (see
// attachHtsThoughtSummary), not the widget's canned buildPatchThoughtSteps
// checklist — labels can legitimately repeat across ticks (e.g. two separate
// GRAPH_PROGRESS updates), so the key includes the index to stay unique.
const HtsThoughtSummary: React.FC<{
  seconds: number;
  steps: HtsTrailStep[];
  // True only for the message currently receiving live updates (matches
  // EARChatUI's live/finished distinction) — shows a pulsing "Thinking…" row
  // instead of "Done", so a still-in-progress turn never claims to be
  // finished just because its narration hasn't grown in the last tick.
  isLive?: boolean;
  // Wall-clock turn start (htsThinkingStartedAtRef), used ONLY while isLive.
  // `seconds` (msg.payload.thoughtSeconds) only refreshes when a poll tick
  // actually brings a new stream_item — between items (which can be several
  // seconds apart) it would otherwise sit frozen and then jump, instead of
  // counting up smoothly. Ticking independently here, the same way
  // HtsThinkingIndicator already does, decouples the displayed duration from
  // poll cadence entirely.
  startedAt?: number | null;
}> = ({ seconds, steps, isLive, startedAt }) => {
  const [liveSeconds, setLiveSeconds] = useState(seconds);

  useEffect(() => {
    if (!isLive || !startedAt) {
      return undefined;
    }

    const updateElapsed = () => {
      setLiveSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [isLive, startedAt]);

  const displaySeconds = isLive && startedAt ? liveSeconds : seconds;

  return (
    <div className='bite-chatkit-thought-block'>
      <div className='bite-chatkit-thought-summary bite-chatkit-thought-summary-static'>
        {formatThoughtDuration(displaySeconds)}
      </div>

      <div className='bite-chatkit-thought-details'>
        {steps.map((step, index) => (
          <div
            key={`${step.text}-${index}`}
            className={`bite-chatkit-thought-step${
              step.level === 'phase'
                ? ' bite-chatkit-thought-phase'
                : step.level === 'finding'
                ? ' bite-chatkit-thought-finding'
                : ''
            }`}
          >
            <span aria-hidden='true' />
            <div>{step.text}</div>
          </div>
        ))}
        {isLive ? (
          <div className='bite-chatkit-thought-step bite-chatkit-thought-step-thinking'>
            <span aria-hidden='true' />
            <div>Thinking…</div>
          </div>
        ) : (
          <div className='bite-chatkit-thought-step bite-chatkit-thought-step-done'>
            <span aria-hidden='true' />
            <div>Done</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Shown ONLY for the brief window before the current turn's bubble has any
// real interim-note narration yet (see the render call site's gating) — a
// live elapsed-time summary plus a plain "Thinking" card. Used to cycle
// through HTS_THINKING_STEPS's 4 canned reassurance phrases as filler ("no
// longer want hardcoded streaming messages" — that's exactly what those
// were), which meant fabricated-sounding specifics like "Reviewing tariff
// chapters" could show even when nothing like that was actually happening.
// Removed; the spinner + duration already convey "still working" honestly.
// Ticks its own 1s timer keyed to when the turn actually started (see
// htsThinkingStartedAtRef), independent of this bubble's own 800ms display
// delay.
const HtsThinkingIndicator: React.FC<{ startedAt: number }> = ({
  startedAt,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
  );

  useEffect(() => {
    const updateElapsed = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [startedAt]);

  return (
    <div
      className='bite-chatkit-thinking-wrap'
      style={{ fontFamily: HTS_FONT_FAMILY }}
    >
      <div className='bite-chatkit-thought-summary bite-chatkit-thought-summary-static'>
        {formatThoughtDuration(elapsedSeconds)}
      </div>
      <div className='bite-chatkit-thinking-card'>
        <div className='bite-chatkit-thinking-title'>
          <span aria-hidden='true' />
          Thinking
        </div>
      </div>
    </div>
  );
};

const ChatUI: React.FC<any> = ({ userHasMonitor }) => {
  const history = useHistory();
  const location = useLocation<{ resumeImportTicket?: any }>();
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  // Set when the team's one trial run of the Import Workflow has already been
  // spent (bubbled up from CommoditiesWorkflow via ImportWorkflowScreening's
  // onTrialLimitReached). Replaces the chat/composer with TrialRunUsedPanel
  // for the rest of this mount -- see the "Gate AI workflows..." ticket.
  const [trialLimitReached, setTrialLimitReached] = useState(false);
  // The old `id: Date.now()` pattern only had millisecond resolution — two
  // messages created synchronously back-to-back (e.g. a user reply
  // immediately followed by the next bot prompt, both pushed in the same
  // handler with no delay between them) can get the SAME id. Since
  // `key={msg.id}` is used when rendering the list, and other flows look
  // messages up by id, a collision like that causes React's "two children
  // with the same key" duplication (this is exactly what caused
  // EARChatUI.tsx's Schedule B section to show its "Thought for Ns" block
  // twice — same `Date.now()`-as-id pattern). A monotonic counter guarantees
  // every live message gets a distinct id.
  const messageIdCounterRef = useRef(0);
  const nextMessageId = () => {
    messageIdCounterRef.current += 1;
    return Date.now() * 1000 + messageIdCounterRef.current;
  };
  const [input, setInput] = useState('');
  const [inputHTS, setInputHTS] = useState('');
  const [originCntry, setOriginCntry] = useState('');
  const { cur_user, ticketDraft, setTicketDraft, resetTicketDraft } =
    useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();

  // Files the user attaches to a turn in the first (classification) stage. They upload to Classify's own
  // endpoint on Send, and the turn names them by id; the rules and the upload live in the adapter. The limits
  // (files per message, pages and sizes) are the server's: the defaults until the real ones arrive.
  const attachmentLimits = useAttachmentLimits(apiUrl);
  const attachmentAdapter = useMemo(
    () => makeClassifyAttachmentAdapter(apiUrl, attachmentLimits),
    [attachmentLimits],
  );
  const attachments = useWorkflowAttachments(attachmentAdapter, {
    notify: (message, variant) =>
      enqueueSnackbar(message, { variant: variant }),
  });
  const markAttachmentsSent = attachments.markSent;
  const selectedTeamId = cur_user?.selected_team?.id;

  // Checked right before the FIRST classification call (not on mount, not on
  // continuation messages) -- same "kickoff only" moment the other 3
  // workflows gate on. Import Workflow's HTS classification is a separate,
  // expensive (real LLM cost) step that runs BEFORE the actual gated step
  // (/evaluate_import_threat), so without this a trial-exhausted team would
  // burn a full classification every time before ever learning they're
  // blocked. Read-only; does not reserve/spend anything itself -- the real
  // gate still fails closed regardless. See the "Gate AI workflows..." ticket.
  const checkImportTrialLimitReached = useCallback(async (): Promise<boolean> => {
    const teamId = cur_user.selected_team?.id;
    if (!teamId) return false;
    try {
      const { data } = await axios.get(`${apiUrl}/ai-workflows/trial-status`, {
        params: {
          group_id: teamId,
          bucket: 'import',
          run_id: getWorkflowRunId('import_workflow', teamId),
        },
        headers: { Authorization: `Bearer ${KeycloakService.getToken()}` },
      });
      // The run that holds the trial (its classification reserved it) may start again: the page
      // reloaded, or its thread expired. Only a trial held by another run blocks.
      return Boolean(data?.applicable && data?.used && !data?.this_run);
    } catch {
      // Fail open here on purpose: this is only a pre-check to avoid wasting
      // a classification call, not the actual gate.
      return false;
    }
  }, [cur_user.selected_team?.id]);

  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const [userProductDesc, setUserProductDesc] = useState<string>('');
  const [userProductDescFix, setUserProductDescFix] = useState<string>('');
  const [userECCN, setUserECCN] = useState<string>('');
  const [followupItems, setFollowupItems] = useState<iFollowupItem[]>([]);
  const followupItemsRef = useRef<iFollowupItem[]>([]);
  const [cmdtDesc, setCmdtDesc] = useState<string | null>(null);
  const [HTSCode, setHTSCode] = useState<string | null>(null);
  // HTSCode gets set as soon as ANY candidate hts_code shows up in a patch —
  // including a preliminary heading mentioned mid-reasoning, well before the
  // backend actually confirms a classification (e.g. it may still ask a
  // clarifying question first). isHtsClassificationComplete is the same
  // check finishHtsTurn already uses to decide whether to reveal the
  // CLASSIFICATION_RESULT card — this tracks that same real "done" signal so
  // the right-rail status doesn't say Complete while a clarifying question is
  // still pending.
  const [isHtsResultConfirmed, setIsHtsResultConfirmed] = useState(false);
  // Latest patch's workflow stage — mirrors Classification.tsx's own reviewStage,
  // drives the "Under Review" badge on ClassifyResultsComp for a proposed (not yet
  // confirmed) classification.
  const [reviewStage, setReviewStage] = useState<string>('');
  const [explanatoryNotes, setExplanatoryNotes] = useState<string | null>(null);
  const [sectionNotes, setSectionNotes] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<number>(1);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isModalOpen2, setModalOpen2] = useState(false);
  const [ticketNameOpen, setTicketNameOpen] = useState(false);
  const [isSavedStageOne, setIsSavedStageOne] = useState(false);
  const [isSavedStageTwo, setIsSavedStageTwo] = useState(false);
  const [isSavedStageThree, setIsSavedStageThree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runCheck, setRunCheck] = useState(false);

  const [savingTicket, setSavingTicket] = useState(false);
  const [tariffCalculated, setTariffCalculated] = useState(false);
  const [ticketId, setTicketID] = useState('');
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [disableLeaveWarning, setDisableLeaveWarning] = useState(false);
  const [isStepRunningModalOpen, setIsStepRunningModalOpen] = useState(false);
  const [runningStepLabel, setRunningStepLabel] = useState('');
  const [attemptedStepLabel, setAttemptedStepLabel] = useState('');
  const [crossLinks, setCrossLinks] = useState<CrossRulingLink[]>([]);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [frequency, setFrequency] = useState('Once');
  const [tariffInputs, setTariffInputs] = useState<any>(
    getDefaultTariffInputs(HTSCode || ''),
  );
  const [effectDate, setEffectDate] = useState(new Date());
  const [loadDate, setLoadDate] = useState(new Date());
  const [productValue, setProductValue] = useState(1000);
  const [lineSplitShow, setLineSplitShow] = useState(false);
  const [loadingCountryShow, setLoadingCountryShow] = useState(false);
  const [ammountShow, setAmmountShow] = useState(false);
  const [, setEnabledExclusions] = useState<
    Record<string, boolean>
  >({});
  const [, setEnabledFtas] = useState<Record<string, boolean>>({});
  const [loadingCountry, setLoadingCountry] = useState<string>('');
  const [countryFieldKey, setCountryFieldKey] = useState<string | undefined>(
    undefined,
  );
  const [countryFieldCodes, setCountryFieldCodes] = useState<string[]>([]);
  const [amountInputs, setAmountInputs] = useState<Record<string, number>>({
    kg: 10,
  });
  const [materialPctByKey, setMaterialPctByKey] = useState<
    Record<string, number>
  >({});
  const [tariffData, setTariffData] = useState<any>([]);
  const [dateStrategy, setDateStrategy] = useState<'current' | 'original'>(
    'original',
  );
  const [material, setMaterial] = useState<string>('');
  const [intendedUse, setIntendedUse] = useState<string>('');
  const [physicalState, setPhysicalState] = useState<string>('');
  const allowNavigationRef = useRef(false);
  const leaveSaveInProgressRef = useRef(false);
  const resumeHydratedRef = useRef(false);
  const resumedTicketNameRef = useRef('');
  const [isResumedTicket, setIsResumedTicket] = useState(false);

  const [chatkitStreamData, setChatkitStreamData] = useState<string[]>([]);
  const [finalSummaryText, setFinalSummaryText] = useState<string>('');
  const [finalReasoningTrace, setFinalReasoningTrace] = useState<string[]>([]);
  const [finalFactsReliedOn, setFinalFactsReliedOn] = useState<string[]>([]);
  const [finalLegalBasisSummary, setFinalLegalBasisSummary] =
    useState<string>('');
  const [finalConfidenceNote, setFinalConfidenceNote] = useState<string>('');
  const [finalChapterNumber, setFinalChapterNumber] = useState<string>('');
  const [finalChapterDescription, setFinalChapterDescription] =
    useState<string>('');
  // Classification runs through the SAME message-array/composer flow as
  // every other stage (see sendHtsMessage/pollHtsTurnUntilDone below) — no
  // separately-mounted chat widget, so there's never a hard branch-swap
  // between stage 1 and stage 2/3. htsThreadIdRef/htsPollGenerationRef are
  // this component's own equivalent of what a chat widget would track
  // internally.
  const htsThreadIdRef = useRef<string | null>(null);
  const htsLastSeenStreamCountRef = useRef(0);
  const htsPollGenerationRef = useRef(0);
  // Whether a send that carries files is under way (see handleSend).
  const fileSendInFlightRef = useRef(false);
  // When the current turn's actual API call started — the source of truth for
  // the "Thought for Xs" duration shown on the completed message.
  const htsThinkingStartedAtRef = useRef<number | null>(null);
  // One id per turn (mirrors HtsClassificationChatClaude's `responseId =
  // assistant_${Date.now()}`), reused across every poll tick so the item that's
  // currently "live" for this turn keeps updating the SAME bubble by id-match
  // instead of by fragile "is it the last message" positional guessing.
  const htsResponseIdRef = useRef<string | null>(null);
  // Trail lines that have arrived from the backend but haven't surfaced yet. A 1s
  // poll often returns several at once, which dumped a whole batch into the panel
  // in one frame and then sat still for seconds; releasing them on a timer makes
  // the panel fill at a readable pace across the wait (same approach as
  // HtsClassificationChatClaude).
  const htsPendingTrailStepsRef = useRef<HtsTrailStep[]>([]);
  const htsTrailRevealIntervalRef = useRef<number | null>(null);
  // Whether a turn is in flight — drives a single always-fresh "Thinking" card
  // rendered after the message list (see JSX below), not a message in the
  // array. Matches the widget: `sending` there is never itself a chat message,
  // so it can never end up duplicated alongside a finalized bubble.
  const [htsThinking, setHtsThinking] = useState(false);
  const WORKFLOW_CREDIT_COST = 4; // total, charged via spendCredits across the 3 steps (1 + 1 + 2)
  // Block starting a run when the team can't cover its full charge (spendCredits, 4
  // credits across the steps). Re-read whenever the workflow returns to its start screen.
  const {
    insufficient: insufficientCredits,
    remaining: remainingCredits,
    refresh: refreshCredits,
    isFreeWorkflowTrial: isFreeWorkflowTrial,
  } = useWorkflowCredits(WORKFLOW_CREDIT_COST, 'import');
  // A finished run has been charged; re-read the balance when back at the start screen.
  useEffect(() => {
    if (messages.length === 0) {
      refreshCredits();
    }
  }, [messages.length, refreshCredits]);
  // Steps (1 = HTS Classification, 2 = Tariffs & Duties) already charged or
  // already complete when a saved/recovered run was loaded, so only steps finished live are billed.
  const settledImportStepsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const teamId = cur_user?.selected_team?.id;
    // Step 3 (Customs Flags) is billed by CommoditiesWorkflow when its screening run finishes.
    const stepsDone = [isHtsResultConfirmed, tariffCalculated];
    stepsDone.forEach((done, index) => {
      const step = index + 1;
      if (!done || settledImportStepsRef.current.has(step) || !teamId) return;
      settledImportStepsRef.current.add(step);
      // The team's free trial run: nothing to charge for any of its steps.
      if (isFreeWorkflowTrial()) return;
      // Fire-and-forget: a billing failure must not block the finished step. The server bills
      // each (reference, step) once, so a saved ticket's repeat never double-charges.
      CreditsAPI.spendCredits({
        teamId: teamId,
        source: 'import_workflow',
        referenceId: ticketDraft?._id ? String(ticketDraft._id) : ticketId || null,
        step: step,
        runId: getWorkflowRunId('import_workflow', teamId),
        userId: cur_user?.basics?.id || null,
        userName:
          [cur_user?.basics?.firstName, cur_user?.basics?.lastName].filter(Boolean).join(' ') ||
          null,
        userEmail: cur_user?.basics?.email || null,
      })
        .then((response: any) => {
          if (!response?.success) {
            console.error('[Import] Failed to log credit spend', response);
          }
          refreshCredits();
        })
        .catch((error: unknown) => {
          console.error('[Import] Failed to log credit spend', error);
        });
    });
  }, [isHtsResultConfirmed, tariffCalculated]);
  // htsThinking flips false only once the turn's answer text has already
  // landed (see finishHtsTurn), so "just stopped generating" is exactly the
  // moment the question/answer/result is ready to bring into view — unlike
  // `messages.length`, which never changes for narration or an answer that
  // fills into an existing bubble in place (see appendHtsPatchMessage).
  const chatScrollRef = useWorkflowChatScroll(messages.length, htsThinking);
  const workflowGridRef = useScrollWorkflowIntoView(messages.length > 0);

  const pyApiClient = useMemo(
    () =>
      axios.create({
        baseURL: apiUrl,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    [],
  );

  const handleTariffCalculationDone = useCallback((data: any) => {
    const nextSnapshot = cloneTariffSnapshot(data);
    setTariffData(nextSnapshot);
    const nextEnabledExclusions =
      nextSnapshot?.results?.enabledExclusions ||
      nextSnapshot?.enabledExclusions;
    const nextEnabledFtas =
      nextSnapshot?.results?.enabledFtas || nextSnapshot?.enabledFtas;
    if (nextEnabledExclusions) {
      setEnabledExclusions((prev) =>
        mergeBoolMapsPreferTrue(nextEnabledExclusions, prev),
      );
    }
    if (nextEnabledFtas) {
      setEnabledFtas((prev) => mergeBoolMapsPreferTrue(nextEnabledFtas, prev));
    }

    const results = nextSnapshot?.results?.summaryRows || nextSnapshot?.results;
    if (Array.isArray(results) && results.length > 0) {
      setTariffCalculated(true);
      // Array.prototype.filter always returns a new array, even when nothing
      // is removed. handleTariffCalculationDone re-fires on every exclusion/
      // FTA toggle (not just the initial calculation), and messages is the
      // updateSignal chatScrollRef watches to auto-scroll — an unconditional
      // filter here forces a fresh array identity on every toggle, which
      // yanks the conversation back to the bottom. Only replace the array
      // (and only then) when the placeholder message is actually present.
      setMessages((prev) =>
        prev.some(
          (msg) => msg.text === 'Wait while we process your tariff check...',
        )
          ? prev.filter(
              (msg) =>
                msg.text !== 'Wait while we process your tariff check...',
            )
          : prev,
      );
      setMessages((prev) => {
        if (prev.some((msg) => msg.type === 'TARIFF_RESULT')) {
          return prev;
        }

        const nextMessage: IMessage = {
          id: nextMessageId(),
          sender: 'bot',
          text: '',
          type: 'TARIFF_RESULT',
        };

        return [...prev, nextMessage];
      });
    }
  }, []);

  const resetTariffState = () => {
    setInputHTS('');
    setUserECCN('');
    setOriginCntry('');
    setTariffCalculated(false);
    setRunCheck(false);
    setTariffInputs(getDefaultTariffInputs(''));
    setEffectDate(new Date());
    setLoadDate(new Date());
    setProductValue(1000);
    setLineSplitShow(false);
    setLoadingCountryShow(false);
    setAmmountShow(false);
    setEnabledExclusions({});
    setEnabledFtas({});
    setLoadingCountry('');
    setCountryFieldKey(undefined);
    setCountryFieldCodes([]);
    setAmountInputs({ kg: 10 });
    setMaterialPctByKey({});
    setTariffData([]);
  };

  const resetWorkflowState = (clearDraft = true) => {
    settledImportStepsRef.current.clear();
    if (clearDraft) {
      // "New Import" / discarding the run starts a new billing run; hydrating a saved
      // ticket (clearDraft false) keeps the current one.
      clearWorkflowRunId('import_workflow', cur_user?.selected_team?.id);
      resetTicketDraft();
    }
    setTicketID('');
    setMessages([]);
    setCurrentStage(1);
    setInput('');
    // A fresh session starts with no files: staged ones, ones already uploaded, and an upload still in
    // flight drops its turn instead of landing in the cleared chat.
    attachments.reset();
    setUserProductDesc('');
    setUserProductDescFix('');
    setUserECCN('');
    followupItemsRef.current = [];
    setFollowupItems([]);
    setCmdtDesc(null);
    setExplanatoryNotes('');
    setSectionNotes('');
    setCrossLinks([]);
    setHTSCode('');
    setIsHtsResultConfirmed(false);
    setReviewStage('');
    setIsSavedStageOne(false);
    setIsSavedStageTwo(false);
    setIsSavedStageThree(false);
    setSaving(false);
    setSavingTicket(false);
    setModalOpen(false);
    setModalOpen2(false);
    setTicketNameOpen(false);
    setNewTicketName('');
    setShareEmail([]);
    setFrequency('Once');
    setMaterial('');
    setIntendedUse('');
    setPhysicalState('');
    setIsResumedTicket(false);
    resumedTicketNameRef.current = '';
    setChatkitStreamData([]);
    setFinalSummaryText('');
    setFinalReasoningTrace([]);
    setFinalFactsReliedOn([]);
    setFinalLegalBasisSummary('');
    setFinalConfidenceNote('');
    setFinalChapterNumber('');
    setFinalChapterDescription('');
    htsPollGenerationRef.current += 1; // cancel any in-flight poll/pending reveal from the previous session
    if (htsThreadIdRef.current) {
      pyApiClient
        .post(`/classify/hts/managed-agent/reset/${htsThreadIdRef.current}`)
        .catch(() => undefined);
    }
    htsThreadIdRef.current = null;
    htsLastSeenStreamCountRef.current = 0;
    htsThinkingStartedAtRef.current = null;
    htsResponseIdRef.current = null;
    htsPendingTrailStepsRef.current = [];
    stopHtsTrailReveal();
    setHtsThinking(false);
    resetTariffState();
  };

  const getStableTicketName = () =>
    savedString(ticketDraft?.ticketName) ||
    savedString(newTicketName) ||
    resumedTicketNameRef.current;

  const getTicketDraftForUpdate = () => {
    const stableTicketName = getStableTicketName();

    return stableTicketName
      ? {
          ...ticketDraft,
          ticketName: stableTicketName,
        }
      : ticketDraft;
  };

  const hydrateSavedImportTicket = (ticket: any) => {
    resetWorkflowState(false);

    const commodity = ticket?.commodity || {};
    const savedTariff = normalizeSavedTariffSnapshot(ticket?.tariffResults);
    const savedTariffInputs = savedTariff?.inputs || {};
    const savedFollowUps = Array.isArray(ticket?.followUps)
      ? ticket.followUps
      : [];
    const savedSharedWith = Array.isArray(ticket?.sharedWith)
      ? ticket.sharedWith
      : [];
    // The actual back-and-forth ("what's the material?" / user's reply, etc.)
    // that happened before the classification card is only preserved here —
    // saveTicket/saveDraftStage1 write it via buildHtsChatHistoryFields, but
    // until now nothing read it back, so resuming a ticket only ever showed
    // the reconstructed opening message and result cards, dropping every
    // clarifying question/answer in between.
    const savedObservedMessages: Array<{ role?: string; text?: string }> =
      Array.isArray(ticket?.htsChatHistory?.observed?.messages)
        ? ticket.htsChatHistory.observed.messages
        : [];
    // Normalize on the way in: tickets saved by the previous flow stored
    // crossRulings as plain URL strings (Case.crossRulings is still typed
    // string[]), and the safeCrossLinks filter below reads link.url — so an
    // un-normalized legacy ruling throws in new URL() and silently disappears
    // from the resumed workflow and result card.
    const savedCrossLinks = Array.isArray(ticket?.crossRulings)
      ? normalizeCrossLinks(ticket.crossRulings)
      : [];
    const savedTicketName = savedString(ticket?.ticketName);
    const nextHTS = savedString(
      commodity?.harmonizedScheduleCode || savedTariffInputs?.htsCode,
    );
    const nextOrigin = savedString(
      commodity?.countryOfOrigin || savedTariffInputs?.originCountry,
    );
    const nextDescription =
      savedString(ticket?.classificationValue) ||
      savedString(commodity?.commodityName) ||
      savedString(ticket?.productDescription);
    const nextCommodityName =
      savedString(commodity?.commodityName) || nextDescription;
    const nextExplanatoryNotes = savedString(ticket?.exNotes) || 'N/A';
    const nextChapterNotes = savedString(ticket?.chapterNotes) || 'N/A';
    // Mirror isHtsClassificationComplete on resume: a saved HTS code can be a
    // PRELIMINARY candidate (ticket saved while a clarifying question was still
    // pending). Only treat the classification as confirmed when the same artifacts
    // that gate the live "done" signal — code + commodity + both notes — are all
    // present, so the right rail doesn't read Complete for a mid-clarification resume.
    const nextHtsResultConfirmed = Boolean(
      nextHTS &&
        savedString(commodity?.commodityName) &&
        savedString(ticket?.exNotes) &&
        savedString(ticket?.chapterNotes),
    );
    const hasTariffResults = hasTariffSnapshotResults(savedTariff);
    const hasScreeningResults =
      Boolean(ticket?.results?.allResults) ||
      (Array.isArray(ticket?.aiSums) && ticket.aiSums.length > 0);
    const resumeStage = hasScreeningResults ? 3 : hasTariffResults ? 2 : 1;

    const nextTariffInputs = {
      ...getDefaultTariffInputs(nextHTS),
      ...savedTariffInputs,
      htsCode: savedTariffInputs?.htsCode || nextHTS,
      originCountry: savedTariffInputs?.originCountry || nextOrigin,
      originIso2: savedString(savedTariffInputs?.originIso2),
      loadingCountry:
        savedString(savedTariffInputs?.loadingCountry) ||
        savedString(savedTariffInputs?.loadingCountryName) ||
        nextOrigin,
      transportMethod:
        savedString(savedTariffInputs?.transportMethod) || 'Ocean',
    };

    setTicketDraft(ticket || {});
    setTicketID(ticket?._id || '');
    resumedTicketNameRef.current = savedTicketName;
    setShareEmail(savedSharedWith);
    setFrequency(savedString(ticket?.frequency) || 'Once');
    setUserProductDesc(nextDescription);
    setUserProductDescFix(nextDescription);
    setCmdtDesc(nextCommodityName);
    setHTSCode(nextHTS);
    setIsHtsResultConfirmed(nextHtsResultConfirmed);
    setInputHTS(nextHTS);
    setExplanatoryNotes(nextExplanatoryNotes);
    setSectionNotes(nextChapterNotes);
    setCrossLinks(savedCrossLinks);
    setMaterial(savedString(commodity?.material));
    setIntendedUse(savedString(commodity?.intendedUse));
    setPhysicalState(savedString(commodity?.physicalState));
    followupItemsRef.current = savedFollowUps;
    setFollowupItems(savedFollowUps);
    setOriginCntry(nextOrigin);
    setTariffInputs(nextTariffInputs);
    setEffectDate(
      parseSavedDate(
        savedTariffInputs?.effectDate || savedTariffInputs?.entryDate,
      ),
    );
    setLoadDate(
      parseSavedDate(
        savedTariffInputs?.loadDate || savedTariffInputs?.loadingDate,
      ),
    );
    setProductValue(Number(savedTariffInputs?.productValue) || 1000);
    setLoadingCountry(nextTariffInputs.loadingCountry || '');
    setAmountInputs(
      savedTariff?.amountInputs ||
        savedTariffInputs?.amountInputs || { kg: 10 },
    );
    setMaterialPctByKey(savedTariff?.materialPctByKey || {});
    setEnabledExclusions(
      savedTariff?.results?.enabledExclusions ||
        savedTariff?.enabledExclusions ||
        {},
    );
    setEnabledFtas(
      savedTariff?.results?.enabledFtas || savedTariff?.enabledFtas || {},
    );
    setTariffData(savedTariff);
    setTariffCalculated(hasTariffResults);
    setRunCheck(hasTariffResults);
    if (hasTariffResults) {
    }
    setCurrentStage(resumeStage);
    setIsSavedStageOne(Boolean(ticket?._id && nextHTS));
    setIsSavedStageTwo(Boolean(ticket?._id && hasTariffResults));
    setIsSavedStageThree(Boolean(ticket?._id && hasScreeningResults));
    // A loaded ticket's finished steps were already billed (or predate step billing).
    if (nextHTS) settledImportStepsRef.current.add(1);
    if (hasTariffResults) settledImportStepsRef.current.add(2);
    if (hasScreeningResults) settledImportStepsRef.current.add(3);
    setIsResumedTicket(true);

    const hydratedMessages: IMessage[] = [];
    const idBase = Date.now();
    if (savedObservedMessages.length > 0) {
      // Rebuild the real clarifying-question conversation from the saved
      // transcript instead of collapsing it into one reconstructed message.
      savedObservedMessages.forEach((entry, index) => {
        const text = savedString(entry?.text);
        if (!text) {
          return;
        }
        const isUser = entry?.role === 'user';
        hydratedMessages.push({
          id: idBase + index,
          sender: isUser ? 'user' : 'bot',
          text: text,
          ...(isUser ? {} : { type: 'HTS_TEXT' }),
        });
      });
    } else if (nextDescription) {
      // Older tickets saved before htsChatHistory.observed.messages existed —
      // fall back to a single reconstructed opening message.
      hydratedMessages.push({
        id: idBase,
        sender: 'user',
        text: buildInitialUserMessageText(
          nextDescription,
          savedString(commodity?.material),
          savedString(commodity?.intendedUse),
          savedString(commodity?.physicalState),
        ),
      });
    }
    const cardIdBase = idBase + savedObservedMessages.length + 1;
    if (nextHTS) {
      hydratedMessages.push({
        id: cardIdBase,
        sender: 'bot',
        text: '',
        type: 'CLASSIFICATION_RESULT',
      });
    }
    if (hasTariffResults || resumeStage >= 2) {
      hydratedMessages.push({
        id: cardIdBase + 1,
        sender: 'bot',
        text: '',
        type: 'TARIFF_RESULT',
      });
    }
    if (hasScreeningResults || resumeStage >= 3) {
      hydratedMessages.push({
        id: cardIdBase + 2,
        sender: 'bot',
        text: '',
        type: 'SCREEN_RESULT',
      });
    }

    setMessages(hydratedMessages);
  };

  const latestMessage = messages[messages.length - 1];
  // Hoisted above isStageBusy (originally computed further down, alongside
  // importProgressSteps) because isCurrentStageUnresolved below needs it too.
  const hasImportStarted =
    messages.some((message) => message.sender === 'user') ||
    Boolean(userProductDesc || inputHTS || userECCN);
  // Whether the active stage is literally streaming right now — gates the
  // composer (isMessageInputDisabled) and the header's Idle/Responding pill.
  // Keep this narrow: it must go false the instant the bot pauses on a
  // clarification question, or the composer stays disabled and the user can
  // never answer it.
  const isStageBusy =
    currentStage === 1
      ? htsThinking
      : latestMessage?.sender === 'bot' && latestMessage.text === 'Thinking...';
  const isMessageInputDisabled = !userHasMonitor || isStageBusy;
  // File attachments are offered in the first (classification) stage only; later stages are tariffs and
  // screening, where a file means nothing. Without the prop the composer is exactly what it was.
  const composerAttachments: WorkflowComposerAttachments | undefined =
    currentStage === 1
      ? {
          files: attachments.stagedFiles,
          onFiles: attachments.stage,
          onRemove: attachments.remove,
          accept: attachments.accept,
          disabled: attachments.isUploading,
          label: attachmentHint(attachmentLimits),
          testIdPrefix: 'import',
        }
      : undefined;
  // Whether the active stage still has work outstanding — used only to block
  // the right-side progress panel from jumping to a different stage. Broader
  // than isStageBusy on purpose: stage 1 counts as unresolved while paused on
  // a clarification question awaiting the user's reply (hasImportStarted &&
  // !isHtsResultConfirmed), not just while htsThinking is actively streaming —
  // otherwise a Tariffs/Customs click mid-conversation slips through and marks
  // HTS Classification "Skipped". Must NOT feed isMessageInputDisabled/the
  // status pill above, or the composer stays disabled for that same gap.
  const isCurrentStageUnresolved =
    currentStage === 1
      ? htsThinking || (hasImportStarted && !isHtsResultConfirmed)
      : isStageBusy;

  useEffect(() => {
    if (isMessageInputDisabled || isModalOpen) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      composerInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [isMessageInputDisabled, isModalOpen]);

  useEffect(() => {
    followupItemsRef.current = followupItems;
  }, [followupItems]);

  // Mirrors every field hydrateSavedImportTicket sets, minus the ones
  // sourced from an actual saved-ticket record shape (tariff snapshot,
  // shared-with list, etc.) -- this restores raw component state directly,
  // not a server ticket payload, so there's no shape translation needed.
  const applyRecoveredChatState = (state: ImportChatRecoverableState) => {
    if (state.isHtsResultConfirmed) settledImportStepsRef.current.add(1);
    if (state.currentStage >= 3) settledImportStepsRef.current.add(2);
    if (state.messages.some((message) => message.type === 'SCREEN_RESULT')) {
      settledImportStepsRef.current.add(3);
    }
    setMessages(state.messages);
    setInput(state.input);
    setCurrentStage(state.currentStage);
    setUserProductDesc(state.userProductDesc);
    setUserProductDescFix(state.userProductDescFix);
    setUserECCN(state.userECCN);
    setCmdtDesc(state.cmdtDesc);
    setHTSCode(state.HTSCode);
    setIsHtsResultConfirmed(state.isHtsResultConfirmed);
    setInputHTS(state.inputHTS);
    setExplanatoryNotes(state.explanatoryNotes);
    setSectionNotes(state.sectionNotes);
    setCrossLinks(state.crossLinks);
    setOriginCntry(state.originCntry);
    setTicketID(state.ticketId);
    setMaterial(state.material);
    setIntendedUse(state.intendedUse);
    setPhysicalState(state.physicalState);
    followupItemsRef.current = state.followupItems;
    setFollowupItems(state.followupItems);
    setNewTicketName(state.newTicketName);
  };

  // Keeps the recoverable registry pointed at the latest values so a forced
  // re-login redirect (see query-client.ts's handleAuthError) can snapshot
  // whatever's actually on screen at that moment. Written directly during
  // render rather than via a useEffect with a giant dependency array --
  // cheap, and always fresh by the time a capture could possibly happen.
  const recoverableStateRef = useRef<ImportChatRecoverableState>(
    {} as ImportChatRecoverableState,
  );
  recoverableStateRef.current = {
    messages: sanitizeMessagesForRecovery(messages),
    input: input,
    currentStage: currentStage,
    userProductDesc: userProductDesc,
    userProductDescFix: userProductDescFix,
    userECCN: userECCN,
    cmdtDesc: cmdtDesc,
    HTSCode: HTSCode,
    isHtsResultConfirmed: isHtsResultConfirmed,
    inputHTS: inputHTS,
    explanatoryNotes: explanatoryNotes,
    sectionNotes: sectionNotes,
    crossLinks: crossLinks,
    originCntry: originCntry,
    ticketId: ticketId,
    material: material,
    intendedUse: intendedUse,
    physicalState: physicalState,
    followupItems: followupItems,
    newTicketName: newTicketName,
  };

  useEffect(() => {
    return registerRecoverable(
      IMPORT_CHAT_RECOVERY_KEY,
      () => recoverableStateRef.current,
    );
  }, []);

  useEffect(() => {
    const resumeTicket = location.state?.resumeImportTicket;

    if (
      resumeTicket?.docType === 'Import Unified' &&
      !resumeHydratedRef.current
    ) {
      resumeHydratedRef.current = true;
      // A resumed saved ticket is a new billing run; its finished steps are marked billed in
      // hydrateSavedImportTicket, so only steps completed from here on are charged.
      clearWorkflowRunId('import_workflow', cur_user?.selected_team?.id);
      hydrateSavedImportTicket(resumeTicket);
      return;
    }

    const recovered = loadDraft<ImportChatRecoverableState>(
      IMPORT_CHAT_RECOVERY_KEY,
    );
    if (recovered) {
      applyRecoveredChatState(recovered);
      return;
    }

    resetWorkflowState(true);
  }, []);

  const leaveWarningStorageKey = useMemo(() => {
    const userId = cur_user?.basics?.id || 'anon';
    return `import-workflow-leave-warning-optout-${userId}`;
  }, [cur_user?.basics?.id]);

  useEffect(() => {
    const localOptOut = localStorage.getItem(leaveWarningStorageKey) === 'true';

    setDisableLeaveWarning(localOptOut);
  }, [leaveWarningStorageKey]);

  const hasUnsavedWorkflowWork = useMemo(() => {
    const hasDraftData = Object.keys(ticketDraft || {}).length > 0;

    return (
      messages.length > 0 ||
      Boolean(input.trim()) ||
      Boolean(userProductDesc) ||
      Boolean(userProductDescFix) ||
      Boolean(material) ||
      Boolean(intendedUse) ||
      Boolean(physicalState) ||
      Boolean(HTSCode) ||
      Boolean(cmdtDesc) ||
      Boolean(originCntry) ||
      Boolean(tariffInputs.htsCode) ||
      Boolean(tariffInputs.originCountry) ||
      hasTariffSnapshotResults(tariffData) ||
      hasDraftData
    );
  }, [
    HTSCode,
    cmdtDesc,
    input,
    intendedUse,
    material,
    messages.length,
    originCntry,
    physicalState,
    tariffData,
    tariffInputs.htsCode,
    tariffInputs.originCountry,
    ticketDraft,
    userProductDesc,
    userProductDescFix,
  ]);

  const isWorkflowSavedForCurrentStage = useMemo(() => {
    const hasSavedTicketId = Boolean(ticketId || ticketDraft?._id);

    if (!hasSavedTicketId) {
      return false;
    }

    if (currentStage <= 1) {
      return isSavedStageOne;
    }
    if (currentStage === 2) {
      return isSavedStageTwo;
    }
    return isSavedStageThree;
  }, [
    currentStage,
    isSavedStageOne,
    isSavedStageThree,
    isSavedStageTwo,
    ticketDraft?._id,
    ticketId,
  ]);

  const shouldWarnOnLeave =
    userHasMonitor &&
    !disableLeaveWarning &&
    hasUnsavedWorkflowWork &&
    !isWorkflowSavedForCurrentStage;

  useEffect(() => {
    if (!shouldWarnOnLeave) {
      return undefined;
    }

    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [shouldWarnOnLeave]);

  const navigateToPendingLocation = useCallback(() => {
    if (!pendingLocation) {
      return;
    }

    history.push({
      pathname: pendingLocation.pathname,
      search: pendingLocation.search,
      hash: pendingLocation.hash,
      state: pendingLocation.state,
    });
  }, [history, pendingLocation]);

  const continueToPendingLocation = useCallback(() => {
    allowNavigationRef.current = true;
    navigateToPendingLocation();
    window.setTimeout(() => {
      allowNavigationRef.current = false;
      setPendingLocation(null);
    }, 0);
  }, [navigateToPendingLocation]);

  const completePendingLeaveAfterSave = useCallback(() => {
    if (!leaveSaveInProgressRef.current) {
      return;
    }

    leaveSaveInProgressRef.current = false;
    resetWorkflowState(true);
    continueToPendingLocation();
  }, [continueToPendingLocation]);

  const cancelPendingLeaveSave = () => {
    if (!leaveSaveInProgressRef.current) {
      return;
    }

    leaveSaveInProgressRef.current = false;
    setPendingLocation(null);
  };

  const handleBlockedNavigation = (nextLocation: any) => {
    if (allowNavigationRef.current) {
      return true;
    }

    if (!shouldWarnOnLeave) {
      return true;
    }

    setPendingLocation(nextLocation);
    setIsLeaveModalOpen(true);
    return false;
  };

  const handleLeaveWithoutSaving = () => {
    setIsLeaveModalOpen(false);
    resetWorkflowState(true);
    continueToPendingLocation();
  };

  const handleLeaveAndOptOut = () => {
    localStorage.setItem(leaveWarningStorageKey, 'true');
    setDisableLeaveWarning(true);
    handleLeaveWithoutSaving();
  };

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  // Applies one live-classification patch to component state. Called once per
  // new stream item while polling a turn (see pollHtsTurnUntilDone) — the same
  // field-by-field update the old /ws/classify stream effect did, just fed by
  // the Claude managed-agent's polling response instead of a WebSocket chunk.
  const applyHtsPatch = useCallback((patch: HtsUiPatch) => {
    const latest = patch?.latest;
    if (!latest) return;

    if (latest.stage) {
      setReviewStage(latest.stage);
    }

    if (patch?.stream_item) {
      // Always append — this state is never rendered here, it is only persisted
      // (aiStream / htsChatHistory.observed.streamItems), and the displayed
      // bubble's replace-stage semantics live in appendHtsPatchMessage below.
      // Classification.tsx collapses replace stages in its own copy of this
      // state because there it IS the right-pane stream; doing the same here
      // would save a one-item transcript, where the widget this mirrors
      // (HtsClassificationChatClaude's observedStreamItemsRef) keeps the whole
      // run. Capped like the widget so a long classification can't grow the
      // saved ticket without bound.
      setChatkitStreamData((prev) =>
        [...prev, patch.stream_item].slice(-MAX_STREAM_ITEMS),
      );
    }

    const cleanHtsCode = cleanHtsValue(latest.hts_code);
    const cleanCmdtDesc = cleanHtsValue(latest.cmdt_desc);

    if (cleanHtsCode) {
      setHTSCode(cleanHtsCode);
    }
    if (cleanCmdtDesc) {
      setCmdtDesc(cleanCmdtDesc);
    }
    if (isHtsClassificationComplete(latest)) {
      setIsHtsResultConfirmed(true);
    }
    if (latest.explanatory_notes) {
      setExplanatoryNotes(latest.explanatory_notes);
    }
    if (latest.section_chapter_notes) {
      setSectionNotes(latest.section_chapter_notes);
    }
    if (latest.cross_data_links) {
      setCrossLinks(normalizeCrossLinks(latest.cross_data_links));
    }
    if (latest.followup_items) {
      setFollowupItems(latest.followup_items as any);
    }

    const cleanSummaryText =
      cleanHtsValue(latest.summary_text) ||
      cleanHtsValue(latest.user_facing_message);
    if (cleanSummaryText) {
      setFinalSummaryText(cleanSummaryText);
    }
    if (latest.reasoning_trace) {
      setFinalReasoningTrace(
        latest.reasoning_trace.map(cleanHtsValue).filter(Boolean),
      );
    }
    if (latest.facts_relied_on) {
      setFinalFactsReliedOn(
        latest.facts_relied_on.map(cleanHtsValue).filter(Boolean),
      );
    }
    const cleanLegalBasisSummary = cleanHtsValue(latest.legal_basis_summary);
    if (cleanLegalBasisSummary) {
      setFinalLegalBasisSummary(cleanLegalBasisSummary);
    }
    const cleanConfidenceNote = cleanHtsValue(latest.confidence_note);
    if (cleanConfidenceNote) {
      setFinalConfidenceNote(cleanConfidenceNote);
    }
    if (latest.chapter_number) {
      setFinalChapterNumber(latest.chapter_number);
    }
    if (latest.chapter_description) {
      setFinalChapterDescription(latest.chapter_description);
    }
  }, []);

  // Mirrors HtsClassificationChatClaude's own appendPatchMessage exactly: a
  // given stream item's message id is stable for as long as it stays "the
  // current live bubble" for this turn (itemResponseId, passed in by the
  // poller below) — an existing-id match REPLACES that same bubble in place;
  // failing that, a REPLACE-stage item still supersedes the last bot bubble;
  // otherwise it's appended fresh. `shouldStream` (not a positional "is this
  // the last message" guess) is what tells HtsMarkdownMessage whether to type
  // this update in; HtsMarkdownMessage itself only animates the first time per
  // message id, so a later update (e.g. a thought-summary attaching to an
  // already-typed bubble) snaps in.
  const appendHtsPatchMessage = useCallback(
    (patch: HtsUiPatch, itemResponseId: string) => {
      const text = buildPatchDisplayText(patch);
      if (!text) return;

      const stage = patch.latest?.stage || '';
      // Anything that isn't a recognised answer stage is narration: it belongs in
      // the thought trail (routed there by the poll loop), never the transcript.
      // Previously PHASE_START/FINDING — and any interim stage the backend adds
      // later — fell through here and became their own chat bubbles.
      if (!HTS_ANSWER_STAGES.has(stage)) return;

      // Everything past this point IS the turn's answer, so it always types in.
      // The old `isInterimNote`/`leavingNote` bookkeeping existed to stream only
      // the first real content following an interim note; with interim notes no
      // longer becoming messages at all, no message can carry that flag.
      setMessages((prev) => {
        const existingIndex = prev.findIndex(
          (msg) => msg.id === itemResponseId,
        );
        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = {
            ...next[existingIndex],
            text: text,
            payload: {
              ...next[existingIndex].payload,
              shouldStream: true,
            },
          };
          return next;
        }

        const last = prev[prev.length - 1];
        if (
          last?.sender === 'bot' &&
          last.type === 'HTS_TEXT' &&
          HTS_REPLACE_STAGES.has(stage)
        ) {
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              id: itemResponseId,
              text: text,
              payload: { ...last.payload, shouldStream: true },
            },
          ];
        }

        return [
          ...prev,
          {
            id: itemResponseId,
            sender: 'bot',
            text: text,
            type: 'HTS_TEXT',
            payload: { shouldStream: true },
          },
        ];
      });
    },
    [],
  );

  // Mirrors attachThoughtSummary: attaches "Thought for Xs" to whichever
  // bubble currently holds itemResponseId WITHOUT touching its text, so it
  // never re-triggers HtsMarkdownMessage's typewriter (that effect only
  // depends on [animate, text]). thoughtSeconds reads htsThinkingStartedAtRef,
  // set once per turn in sendHtsMessage and never reset, so it's the
  // cumulative time since the user's message was sent, same as the widget.
  //
  // `newSteps` is every REAL narration line from that poll batch, each already
  // levelled (phase / finding / step) by htsTrailLevelForStage. They're appended
  // to whatever this message's panel already has, never replacing or reordering
  // earlier steps, so the panel grows with each new line — matching EARChatUI's
  // append-only "Thought" panel. Called on
  // every tick regardless (even ones with no new step) so the duration
  // keeps counting up to the final answer, not just to the last interim
  // note. This merge, and buildPatchThoughtSteps itself, stay local to
  // ChatUI.tsx — the shared HtsClassificationChat.tsx module (and
  // HtsClassificationChatClaude's own canned-checklist summary) are
  // untouched.
  const attachHtsThoughtSummary = useCallback(
    (itemResponseId: string, newSteps: HtsTrailStep[] = []) => {
      const thoughtSeconds = htsThinkingStartedAtRef.current
        ? Math.max(
            0,
            Math.floor((Date.now() - htsThinkingStartedAtRef.current) / 1000),
          )
        : 0;

      // Append each new line, skipping one identical to the line before it
      // (repeated identical narration would otherwise stack up).
      const mergeSteps = (existing: HtsTrailStep[]) =>
        newSteps.reduce(
          (acc: HtsTrailStep[], step) =>
            acc[acc.length - 1]?.text === step.text ? acc : [...acc, step],
          existing,
        );

      setMessages((prev) => {
        // Narration almost always arrives BEFORE the turn's answer, and answer
        // patches are the only thing that creates this message. Attaching only to
        // an existing message therefore silently discarded every line collected
        // up to that point — the whole live trail. Create the carrier here (empty
        // text renders as trail-only; the answer fills the text in later).
        if (!prev.some((msg) => msg.id === itemResponseId)) {
          if (newSteps.length === 0) return prev;
          return [
            ...prev,
            {
              id: itemResponseId,
              sender: 'bot',
              text: '',
              type: 'HTS_TEXT',
              payload: {
                thoughtSeconds: thoughtSeconds,
                thoughtSteps: mergeSteps([]),
              },
            },
          ];
        }

        return prev.map((msg) => {
          if (msg.id !== itemResponseId) {
            return msg;
          }

          const existingSteps: HtsTrailStep[] = msg.payload?.thoughtSteps || [];
          const mergedSteps = mergeSteps(existingSteps);

          return {
            ...msg,
            payload: {
              ...msg.payload,
              thoughtSeconds: thoughtSeconds,
              // Stored raw (possibly empty) so other code (the live indicator
              // below) can tell "no real narration yet" apart from "already has
              // some". There is no canned-checklist fallback any more — an empty
              // trail renders as an empty trail.
              thoughtSteps: mergedSteps,
            },
          };
        });
      });
    },
    [],
  );

  const stopHtsTrailReveal = useCallback(() => {
    if (htsTrailRevealIntervalRef.current !== null) {
      window.clearInterval(htsTrailRevealIntervalRef.current);
      htsTrailRevealIntervalRef.current = null;
    }
  }, []);

  // Releases one queued line into the visible panel per tick.
  const revealNextHtsTrailStep = useCallback(() => {
    if (htsPendingTrailStepsRef.current.length === 0) return;
    const responseId = htsResponseIdRef.current;
    if (!responseId) return;
    const [next, ...rest] = htsPendingTrailStepsRef.current;
    htsPendingTrailStepsRef.current = rest;
    attachHtsThoughtSummary(responseId, [next]);
  }, [attachHtsThoughtSummary]);

  // Releases everything still queued at once. The pacing exists to fill a wait,
  // so it must never delay the turn's answer or strand lines behind a panel that
  // has already stopped growing.
  const flushHtsTrailSteps = useCallback(() => {
    if (htsPendingTrailStepsRef.current.length === 0) return;
    const responseId = htsResponseIdRef.current;
    const queued = htsPendingTrailStepsRef.current;
    htsPendingTrailStepsRef.current = [];
    if (responseId) {
      attachHtsThoughtSummary(responseId, queued);
    }
  }, [attachHtsThoughtSummary]);

  // Called once a turn stops running (turn_status !== 'running'). Every
  // step's text was already finalized progressively as it arrived (see
  // appendHtsPatchMessage in the poll loop below) — this just ends the
  // standalone "Thinking" indicator (mirrors the widget's endThinking()),
  // surfaces errors, and — once classification is complete — schedules the
  // CLASSIFICATION_RESULT card 1.5s later, same append-only `messages`
  // array, no view swap, matching how TARIFF_RESULT -> SCREEN_RESULT already
  // transitions.
  const finishHtsTurn = useCallback(
    (
      outcome: {
        latest?: HtsUiPatch['latest'] | null;
        errorMessage?: string;
      },
      generation: number,
    ) => {
      setHtsThinking(false);
      // The turn is over: anything still queued must surface now rather than
      // trickling out after the answer is already on screen.
      stopHtsTrailReveal();
      flushHtsTrailSteps();

      if (outcome.errorMessage) {
        setMessages((prev) => [
          ...prev,
          { id: nextMessageId(), sender: 'bot', text: outcome.errorMessage as string },
        ]);
        return;
      }

      const latest = outcome.latest || {};

      if (isHtsClassificationComplete(latest)) {
        setTariffInputs(getDefaultTariffInputs(cleanHtsValue(latest.hts_code)));

        window.setTimeout(() => {
          if (htsPollGenerationRef.current !== generation) return;
          setMessages((prev) => {
            if (prev.some((msg) => msg.type === 'CLASSIFICATION_RESULT')) {
              return prev;
            }
            return [
              ...prev,
              {
                id: nextMessageId(),
                sender: 'bot',
                text: '',
                type: 'CLASSIFICATION_RESULT',
              },
            ];
          });
        }, 1500);
      }
    },
    [],
  );

  // Polls GET /classify/hts/managed-agent/state until the turn stops running.
  // Mirrors the widget's processStateResponse/pollTurnUntilDone: every new
  // stream item updates background state (applyHtsPatch, with the FULL
  // `latest` every time) and is finalized into a message keyed off this
  // turn's stable base id — only the batch's LAST item reuses that base id
  // (so it keeps updating "the current live bubble" across ticks); earlier
  // items in the same batch get a permanently distinct suffixed id, exactly
  // like the widget's `isLast ? responseId : \`${responseId}_${index}\``.
  // `generation` lets a reset ("New Import") mid-turn cancel a stale poll —
  // or a stale pending CLASSIFICATION_RESULT reveal — instead of it
  // clobbering the next session's state once it eventually resolves.
  const pollHtsTurnUntilDone = useCallback(
    async (threadId: string, generation: number) => {
      const POLL_MS = 1000;
      const MAX_POLLS = 300;
      // The item that mattered for this turn may have arrived several polls
      // before turn_status actually flips away from 'running' — track it
      // across iterations instead of only looking at the LAST poll's
      // (possibly empty) newItems, so a quiet final poll can still fall back
      // to finalizing with whatever `latest` was last seen.
      let lastLatestSeen: HtsUiPatch['latest'] = {};

      for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
        if (htsPollGenerationRef.current !== generation) return;

        let data: any = null;
        try {
          const response = await pyApiClient.get(
            `/classify/hts/managed-agent/state/${threadId}`,
          );
          data = response.data;
        } catch (err: any) {
          if (htsPollGenerationRef.current !== generation) return;

          // The agent keeps running server-side regardless of whether we can
          // reach it, so a dropped connection or a 5xx must not end the local
          // turn — sendHtsMessage's catch would finalize it as an error and the
          // user's next message would then collide with a turn still in flight.
          // Spend a poll attempt and retry; MAX_POLLS still bounds the wait.
          // 4xx (notably the 404 that means the thread is gone) stays fatal so
          // the caller can clear htsThreadIdRef and start a fresh session.
          const status = err?.response?.status;
          if (status && status < 500) {
            throw err;
          }

          await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
          continue;
        }

        if (htsPollGenerationRef.current !== generation) return;

        const streamItems: string[] = data?.stream_items || [];
        const latest = data?.latest || {};
        // Absolute position in the full stream, not the index within this
        // poll's batch — newItems is re-sliced fresh every tick, so a
        // batch-relative index restarts at 0 each time and a later batch's
        // non-last item could collide with (and overwrite) an earlier one's
        // permanently-suffixed id.
        const firstNewItemIndex = htsLastSeenStreamCountRef.current;
        const newItems = streamItems.slice(firstNewItemIndex);
        htsLastSeenStreamCountRef.current = streamItems.length;

        const baseResponseId =
          htsResponseIdRef.current || `hts_assistant_${generation}`;

        // One stage per stream_item, positionally aligned. `latest` is a single
        // snapshot the backend overwrites on every publish, so on a poll that
        // returns several items it only describes the LAST — using it for all of
        // them mis-attributed every earlier item's stage.
        const streamStages: (string | null)[] = data?.stream_stages || [];
        const newTrailSteps: HtsTrailStep[] = [];

        newItems.forEach((item: string, index: number) => {
          const isLastInBatch = index === newItems.length - 1;
          const absoluteItemIndex = firstNewItemIndex + index;
          const itemResponseId = isLastInBatch
            ? baseResponseId
            : `${baseResponseId}_${absoluteItemIndex}`;
          const itemStage = streamStages[absoluteItemIndex] || undefined;
          applyHtsPatch({ stream_item: item, latest: latest });

          const itemPatch: HtsUiPatch = {
            stream_item: item,
            latest: isLastInBatch
              ? latest
              : // Carry only the stage for earlier items — never the shared
                // final-state snapshot, which would misformat them.
                ((itemStage
                  ? { stage: itemStage }
                  : {}) as HtsUiPatch['latest']),
          };

          // Collect narration for the trail; appendHtsPatchMessage ignores
          // anything that isn't an answer stage.
          const level = htsTrailLevelForStage(
            (isLastInBatch ? latest?.stage : itemStage) || '',
          );
          if (level) {
            const stepText = buildPatchDisplayText(itemPatch);
            if (stepText) {
              newTrailSteps.push({ text: stepText, level: level });
            }
          }

          appendHtsPatchMessage(itemPatch, itemResponseId);
        });

        if (newTrailSteps.length > 0) {
          // Queued rather than attached immediately, so a batch of several lines
          // surfaces one at a time instead of all in one frame. Every line from
          // the batch is kept — deriving a single step from the batch's last
          // stage (as this used to) discarded most of the backend's narration.
          htsPendingTrailStepsRef.current = [
            ...htsPendingTrailStepsRef.current,
            ...newTrailSteps,
          ];
        }
        if (newItems.length > 0) {
          // Keeps the panel's elapsed-seconds fresh even on a tick whose lines
          // are all still queued.
          attachHtsThoughtSummary(baseResponseId, []);
        }

        if (Object.keys(latest).length > 0) {
          lastLatestSeen = latest;
          // The turn's final `latest` snapshot can update on a quiet poll
          // (no new stream_items entry, e.g. turn_status flipping away from
          // 'running' on its own tick) — without this, HTSCode/cmdtDesc/etc.
          // stay stale since applyHtsPatch otherwise only runs per new item.
          if (newItems.length === 0) {
            applyHtsPatch({ stream_item: '', latest: latest });
          }
        }

        const turnStatus = data?.turn_status || 'idle';

        if (turnStatus !== 'running') {
          if (htsPollGenerationRef.current !== generation) return;

          if (turnStatus === 'error') {
            finishHtsTurn(
              {
                errorMessage:
                  data?.last_error ||
                  'There was an error during classification.',
              },
              generation,
            );
            return;
          }

          finishHtsTurn({ latest: lastLatestSeen }, generation);
          return;
        }

        await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
      }

      if (htsPollGenerationRef.current === generation) {
        finishHtsTurn(
          {
            errorMessage:
              'Classification is taking longer than expected. Please try again.',
          },
          generation,
        );
      }
    },
    [
      applyHtsPatch,
      appendHtsPatchMessage,
      attachHtsThoughtSummary,
      finishHtsTurn,
      pyApiClient,
    ],
  );

  // Starts a session (first message) or continues one (follow-ups), then
  // polls until that turn is done. This is the direct replacement for the
  // old submitClassification (WS) call — same call site in handleSend, same
  // "Thinking..." placeholder convention, different backend underneath.
  const sendHtsMessage = useCallback(
    async (
      text: string,
      options?: {
        // The files this turn carries, already uploaded.
        attachments?: ClassifyAttachmentRef[];
        // A turn with files checks the trial limit before it uploads them; do not check it twice.
        skipTrialCheck?: boolean;
      },
    ) => {
      const turnAttachments = options?.attachments || [];
      // Kickoff only, before any "Thinking..." UI starts -- continuation
      // messages (an existing thread) are free, same policy as the other 3
      // workflows.
      if (!htsThreadIdRef.current && !options?.skipTrialCheck) {
        const blocked = await checkImportTrialLimitReached();
        if (blocked) {
          setTrialLimitReached(true);
          return;
        }
      }

      htsPollGenerationRef.current += 1;
      const generation = htsPollGenerationRef.current;
      htsThinkingStartedAtRef.current = Date.now();
      htsResponseIdRef.current = `hts_assistant_${Date.now()}`;
      htsPendingTrailStepsRef.current = [];
      stopHtsTrailReveal();
      htsTrailRevealIntervalRef.current = window.setInterval(
        revealNextHtsTrailStep,
        HTS_TRAIL_REVEAL_MS,
      );
      setHtsThinking(true);
      // The request below waits while the server prepares the files; say so rather than sit silent.
      const preparationNote = attachmentPreparationNote(turnAttachments);
      if (preparationNote) {
        htsPendingTrailStepsRef.current = [
          ...htsPendingTrailStepsRef.current,
          { text: preparationNote, level: 'step' },
        ];
      }

      try {
        // Sent only with files, so a turn without any is byte-for-byte what it was before. A turn that
        // carries files must also prove who is asking: the server checks team membership before it
        // reads one.
        const turnFields = attachmentTurnFields(turnAttachments, selectedTeamId);
        const turnConfig = await attachmentTurnConfig(turnAttachments);
        if (!htsThreadIdRef.current) {
          // The Import run starts here, so the server reserves the team's free trial run for it now
          // (the HTS session route): the classification and tariffs are only free once the trial is
          // really held, and a run started after another one took the trial is refused.
          const kickoffFields = selectedTeamId
            ? {
                group_id: selectedTeamId,
                trial_bucket: 'import',
                run_id: getWorkflowRunId('import_workflow', selectedTeamId),
              }
            : {};
          const kickoffConfig = selectedTeamId
            ? {
                ...turnConfig,
                headers: { ...turnConfig?.headers, ...(await classifyAuthHeaders()) },
              }
            : turnConfig;
          const { data } = await pyApiClient.post(
            '/classify/hts/managed-agent/session',
            { product_desc: text, ...turnFields, ...kickoffFields },
            kickoffConfig,
          );
          // A reset while this request was out made the chat a different one. Its answer is not for the files
          // waiting there now, so it must not mark them sent, and it starts no polling.
          if (htsPollGenerationRef.current !== generation) return;
          // The server has taken the turn, files included.
          markAttachmentsSent();
          htsThreadIdRef.current = data.thread_id;
          htsLastSeenStreamCountRef.current = 0;
        } else {
          await pyApiClient.post(
            '/classify/hts/managed-agent/message',
            {
              thread_id: htsThreadIdRef.current,
              message: text,
              ...turnFields,
            },
            turnConfig,
          );
          if (htsPollGenerationRef.current !== generation) return;
          markAttachmentsSent();
        }

        await pollHtsTurnUntilDone(
          htsThreadIdRef.current as string,
          generation,
        );
      } catch (err: any) {
        if (htsPollGenerationRef.current !== generation) return;

        const status = err?.response?.status;

        // A 404 means the backend thread is gone — without clearing it here,
        // htsThreadIdRef keeps pointing at the dead thread, so resending just
        // retries the same expired session and 404s again indefinitely.
        // Clearing it makes the next send take the "no thread yet" branch
        // above and start a fresh managed-agent session automatically.
        if (status === 404) {
          htsThreadIdRef.current = null;
          htsLastSeenStreamCountRef.current = 0;
          htsResponseIdRef.current = null;
        }

        // A 402 at kickoff: the team's one trial run of the Import Workflow is held by another run, so this
        // one can't start (the detail below says so). Block the chat as the pre-check would have.
        if (status === 402) {
          setTrialLimitReached(true);
        }

        // A 413 on a turn with files means the files are over a limit (the detail below says which). They
        // are dropped: kept, every retry would be refused the same way, with no way to take one out.
        if (status === 413 && turnAttachments.length > 0) {
          markAttachmentsSent();
        }

        const detail =
          status === 404
            ? 'This chat session has expired. Send your message again to start a new chat.'
            : toErrorMessage(
                err?.response?.data?.detail ||
                  err?.response?.data?.message ||
                  err?.message,
                'Unable to send message. Please try again.',
              );
        finishHtsTurn({ errorMessage: detail }, generation);
      }
    },
    [
      checkImportTrialLimitReached,
      finishHtsTurn,
      markAttachmentsSent,
      pollHtsTurnUntilDone,
      pyApiClient,
      selectedTeamId,
    ],
  );

  const handleCloseModal = () => {
    cancelPendingLeaveSave();
    setModalOpen(false);
  };

  const handleCloseModal2 = () => {
    cancelPendingLeaveSave();
    setModalOpen2(false);
  };

  const handleTicketNameClose = () => {
    cancelPendingLeaveSave();
    setTicketNameOpen(false);
    setNewTicketName('');
  };

  // A first-stage send that carries files. The files upload first, so a failed upload (the user is told
  // why) changes nothing on screen; only then does the user's bubble appear and the turn go out. Sends
  // without files never come through here.
  const sendTurnWithFiles = async (typedText: string) => {
    const isFirstTurn = !htsThreadIdRef.current;
    const generation = htsPollGenerationRef.current;
    // The trial limit is checked before the files upload, so a blocked team does not leave uploads behind.
    if (isFirstTurn) {
      const blocked = await checkImportTrialLimitReached();
      // The check is a request. If the chat was reset while it was out, this send was for the chat that is
      // gone: it must not add its message to the new one, start a turn there, or block it.
      if (htsPollGenerationRef.current !== generation) return;
      if (blocked) {
        setTrialLimitReached(true);
        return;
      }
    }

    const outcome = await attachments.upload(selectedTeamId);
    if (outcome.status !== 'ready') {
      return;
    }
    const files = outcome.attachments;
    const names = files.map((file) => file.filename);
    // What the backend gets. A turn needs non-empty text (and the first turn's text becomes the product
    // description), so an attachment-only send says what to do with the files.
    const text = typedText || attachmentOnlyMessage(names);
    // What the user sees in their own bubble.
    const displayText = typedText || attachmentSummary(names);
    const isFirstUserMessage = userProductDesc === '';
    if (isFirstUserMessage) {
      setUserProductDesc(text);
    }
    setMessages((prev) => [
      ...prev,
      {
        id: nextMessageId(),
        sender: 'user',
        text: displayText,
        payload: { attachedFilenames: names },
      },
    ]);
    setInput('');
    sendHtsMessage(
      isFirstUserMessage
        ? buildInitialUserMessageText(text, material, intendedUse, physicalState)
        : text,
      { attachments: files, skipTrialCheck: isFirstTurn },
    );
    window.setTimeout(() => {
      composerInputRef.current?.focus();
    }, 0);
  };

  const handleSend = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (
      isMessageInputDisabled ||
      attachments.isUploading ||
      fileSendInFlightRef.current
    ) {
      return;
    }

    // First stage only: a send with files (or with files left over from a send that failed) takes its own
    // path, because the files upload before anything else happens. Every other send is exactly what it was.
    if (
      currentStage === 1 &&
      (attachments.stagedFiles.length > 0 || attachments.hasPendingUploads())
    ) {
      // Nothing marks the chat busy until the trial limit has been checked and the files are uploading, and
      // the text stays in the box until then, so another press of Send in that time would start a second
      // send of the same files. The lock is taken here, in the same event, and held until the send ends.
      fileSendInFlightRef.current = true;
      sendTurnWithFiles(input.trim()).finally(() => {
        fileSendInFlightRef.current = false;
      });
      return;
    }

    const value = input.trim();
    if (!value) {
      return;
    }

    const latestMessage = messages[messages.length - 1];
    const stageOneSaveChoice =
      ticketId && isSavedStageOne ? 'View Saved' : 'Save';
    const stageTwoSaveChoice =
      ticketId && isSavedStageTwo ? 'View Saved' : 'Save';
    const stageThreeSaveChoice =
      ticketId && isSavedStageThree ? 'View Saved' : 'Save Workflow';
    const availableWorkflowChoices =
      latestMessage?.sender !== 'bot'
        ? []
        : latestMessage.type === 'CLASSIFICATION_RESULT'
        ? ['Calculate Tariffs', stageOneSaveChoice]
        : latestMessage.type === 'TARIFF_CAL' && !runCheck
        ? ['Run Check']
        : latestMessage.type === 'TARIFF_RESULT' && tariffCalculated
        ? ['Proceed to Screening', stageTwoSaveChoice]
        : latestMessage.type === 'SCREEN_RESULT'
        ? [stageThreeSaveChoice, 'New Session']
        : [];
    const selectedWorkflowChoice = resolveWorkflowChoice(
      value,
      availableWorkflowChoices,
    );

    if (selectedWorkflowChoice) {
      setInput('');
      if (selectedWorkflowChoice === 'View Saved' && ticketId) {
        history.push(`/monitor/ticket/${ticketId}`);
      } else {
        handleOptionClick(selectedWorkflowChoice);
      }
      window.setTimeout(() => composerInputRef.current?.focus(), 0);
      return;
    }

    const isFirstUserMessage = userProductDesc === '';
    const initialMessageText = buildInitialUserMessageText(
      value,
      material,
      intendedUse,
      physicalState,
    );

    if (isFirstUserMessage) {
      setUserProductDesc(value);
    }
    const isLastMessageImportQuestion =
      messages.length > 0 &&
      typeof messages[messages.length - 1].text === 'string' &&
      messages[messages.length - 1].text ===
        'Please provide enter country of origin?';

    const isAskingDestination =
      messages.length > 0 &&
      typeof messages[messages.length - 1].text === 'string' &&
      messages[messages.length - 1].text === 'Please enter country of origin?';

    const isAskingECCN =
      messages.length > 0 &&
      typeof messages[messages.length - 1].text === 'string' &&
      messages[messages.length - 1].text ===
        'Please provide your HTS Code to begin the Tariff calculation.';

    const isAskingHTS =
      messages.length > 0 &&
      typeof messages[messages.length - 1].text === 'string' &&
      messages[messages.length - 1].text ===
        'Please provide your HTS Code to begin the screening and compliance.';

    // step 2
    if (isAskingECCN) {
      setUserECCN(value);
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), sender: 'user', text: value },
      ]);
      // Ask for destination country
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          sender: 'bot',
          text: 'Please enter country of origin?',
        },
      ]);
      setInput('');
      window.setTimeout(() => {
        composerInputRef.current?.focus();
      }, 0);
      return;
    }

    if (isAskingDestination) {
      setOriginCntry(value); // or setDestinationCntry if separate
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), sender: 'user', text: value },
      ]);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            sender: 'bot',
            text: 'Wait while we process your tariff check...',
          },
        ]);
      }, 400);
      setInput('');
      window.setTimeout(() => {
        composerInputRef.current?.focus();
      }, 0);

      return;
    }

    // step 3
    if (isAskingHTS) {
      setInputHTS(value);
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), sender: 'user', text: value },
      ]);
      // Ask for destination country
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          sender: 'bot',
          text: 'Please provide enter country of origin?',
        },
      ]);
      setInput('');
      window.setTimeout(() => {
        composerInputRef.current?.focus();
      }, 0);
      return;
    }

    if (isLastMessageImportQuestion) {
      const newMessage: IMessage = {
        id: nextMessageId(),
        sender: 'user',
        text: value,
      };
      setMessages((prev) => [...prev, newMessage]);
      // Set origin country FIRST
      setOriginCntry(value);

      if (inputHTS) {
        /** ✅ Example of TariffPage insertion as JSX */
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            sender: 'bot',
            text: 'Wait while we run screening & compliance!',
          },
        ]);
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              id: nextMessageId(),
              sender: 'bot',
              text: '',
              type: 'SCREEN_RESULT',
            },
          ]);
        }, 400);
      }
    } else {
      const newMessage: IMessage = {
        id: nextMessageId(),
        sender: 'user',
        text: isFirstUserMessage ? initialMessageText : value,
      };
      setMessages((prev) => [...prev, newMessage]);
      // Simulate bot response. Stage 1's own "Thinking" indicator is the
      // standalone htsThinking-gated card (set in sendHtsMessage) — it isn't a
      // message in this array at all, so pushing a legacy 'Thinking...' bubble
      // here too would just show a second, redundant indicator.
      if (currentStage !== 1) {
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            { id: nextMessageId(), sender: 'bot', text: 'Thinking...' },
          ]);
        }, 800);
      }
    }
    if (currentStage === 1) {
      sendHtsMessage(isFirstUserMessage ? initialMessageText : value);
    }
    setInput('');
    window.setTimeout(() => {
      composerInputRef.current?.focus();
    }, 0);
  };

  const handleOptionClick = async (option: string) => {
    if (userProductDesc === '') {
      setUserProductDesc(option);
    }
    const isLastMessageImportQuestion =
      messages.length > 0 &&
      typeof messages[messages.length - 1].text === 'string' &&
      messages[messages.length - 1].text ===
        'Which country are you importing from ?';

    if (isLastMessageImportQuestion) {
      const newMessage: IMessage = {
        id: nextMessageId(),
        sender: 'user',
        text: option,
      };
      setMessages((prev) => [...prev, newMessage]);
      // Set origin country FIRST
      setOriginCntry(input);
    }

    if (option === 'Calculate Tariffs') {
      setCurrentStage(2);
      saveDraftStage1('');
      const newMessage: IMessage = {
        id: nextMessageId(),
        sender: 'user',
        text: option,
      };
      setMessages((prev) => [...prev, newMessage]);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            sender: 'bot',
            text: '',
            type: 'TARIFF_CAL',
          },
        ]);
      }, 800);
    } else if (option === 'Run Check') {
      const currentOrigin = tariffInputs.originCountry || originCntry || '';
      setUserECCN(tariffInputs.htsCode);
      if (currentOrigin) {
        const newMessage: IMessage = {
          id: nextMessageId(),
          sender: 'user',
          text: option,
        };
        setMessages((prev) => [...prev, newMessage]);

        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              id: nextMessageId(),
              sender: 'bot',
              text: `Wait while we process your tariff check...`,
            },
          ]);
        }, 400);
        setTimeout(() => {
          setMessages((prev) => {
            if (prev.some((msg) => msg.type === 'TARIFF_RESULT')) {
              return prev;
            }

            return [
              ...prev,
              {
                id: nextMessageId(),
                sender: 'bot',
                text: '',
                type: 'TARIFF_RESULT',
              },
            ];
          });
        }, 800);
        setOriginCntry(currentOrigin);
        setRunCheck(true);
      } else {
        enqueueSnackbar('Please input the origin country!', {
          variant: 'error',
        });
      }
    } else if (option === 'Proceed to Screening') {
      const currentOrigin = tariffInputs.originCountry || originCntry || '';
      if (currentOrigin) {
        setOriginCntry(currentOrigin);
      }
      setCurrentStage(3);
      const newMessage: IMessage = {
        id: nextMessageId(),
        sender: 'user',
        text: option,
      };
      setMessages((prev) => [...prev, newMessage]);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            sender: 'bot',
            text: '',
            type: 'SCREEN_RESULT',
          },
        ]);
      }, 400);
    } else if (option === 'Save' && currentStage === 1) {
      setModalOpen(true);
    } else if (option === 'Save' && currentStage === 2) {
      if (ticketDraft._id) {
        const ticketUpdate = getTicketDraftForUpdate();
        const resp = await CasesAPI.updateCase(ticketUpdate);
        if (resp?.success) {
          setIsSavedStageTwo(true);
          shareEmail.forEach((email) => {
            handleShareTicketNotification(
              ticketUpdate.ticketName,
              cur_user,
              email,
              resp.result._id,
            );
          });
          setSaving(false);
          handleCloseModal2();
          enqueueSnackbar('Successfully created new case', {
            variant: 'success',
          });
        } else if (resp) {
          enqueueSnackbar(
            'Error saving new case. Uploaded file(s) may be too large.',
            {
              variant: 'error',
            },
          );
        }
      } else {
        setModalOpen2(true);
      }
    } else if (option === 'Save Workflow') {
      if (ticketDraft._id) {
        const ticketUpdate = getTicketDraftForUpdate();
        const resp = await CasesAPI.updateCase(ticketUpdate);
        if (resp?.success) {
          setIsSavedStageThree(true);
          shareEmail.forEach((email) => {
            handleShareTicketNotification(
              ticketUpdate.ticketName,
              cur_user,
              email,
              resp.result._id,
            );
          });
          setSaving(false);
          setTicketNameOpen(false);
          setNewTicketName('');
          enqueueSnackbar('Successfully created new case', {
            variant: 'success',
          });
          resetTicketDraft();
        } else if (resp) {
          enqueueSnackbar(
            'Error saving new case. Uploaded file(s) may be too large.',
            {
              variant: 'error',
            },
          );
        }
      } else {
        setTicketNameOpen(true);
      }
    } else if (option === 'New Session') {
      if (shouldWarnOnLeave) {
        setPendingLocation(null);
        setIsLeaveModalOpen(true);
        return;
      }

      resetWorkflowState(true);
      return;
    } else {
      const newMessage: IMessage = {
        id: nextMessageId(),
        sender: 'user',
        text: option,
      };
      setMessages((prev) => [...prev, newMessage]);
      // Simulate bot response
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { id: nextMessageId(), sender: 'bot', text: 'Thinking...' },
        ]);
      }, 800);
    }

    setInput('');
  };

  const handleTicket = async (values: any) => {
    const new_case = {
      groupId: cur_user.selected_team.id,
      ...values.basics,
      ...values,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });
    setTicketDraft(new_case);

    if (resp?.success) {
      setTicketID(resp.result._id);
      setIsSavedStageOne(true);
      enqueueSnackbar('Successfully created new ticket', {
        variant: 'success',
      });
      setTicketDraft({
        ...new_case,
        _id: resp.result._id,
      });
      shareEmail.forEach((email) => {
        // Call to notification sending function here
        handleShareTicketNotification(
          values.ticketName,
          cur_user,
          email,
          resp.result._id,
        );
      });
      setSavingTicket(false);
      setModalOpen(false);
      setNewTicketName('');
      completePendingLeaveAfterSave();
    } else if (resp) {
      const error_msg =
        'Error saving new ticket, please try again and contact the BITE Team if the problem persist';
      enqueueSnackbar(error_msg, { variant: 'error' });
      setSavingTicket(false);
      setModalOpen(false);
    }
  };

  // Builds the htsChatHistory/htsFinalReport fields shared by saveTicket and
  // saveDraftStage1, from this component's own tracked classification state
  // (the conversation lives in `messages` and the classification fields
  // directly on this component — there's no separate widget/snapshot).
  const buildHtsChatHistoryFields = () => {
    const streamItems = chatkitStreamData
      .filter((item): item is string => typeof item === 'string')
      .slice(-MAX_STREAM_ITEMS)
      .map((item) =>
        item.length <= MAX_STREAM_ITEM_LENGTH
          ? item
          : `${item.slice(0, MAX_STREAM_ITEM_LENGTH)}... [truncated]`,
      );
    // The classification conversation is whatever's in `messages` before the
    // CLASSIFICATION_RESULT card (stage 2/3 always appends after that marker,
    // so nothing from later stages can land in this slice). No such marker
    // yet (e.g. a mid-classification "Save Draft") just means everything so
    // far is the classification conversation.
    const classificationEndIndex = messages.findIndex(
      (msg) => msg.type === 'CLASSIFICATION_RESULT',
    );
    const classificationMessages =
      classificationEndIndex === -1
        ? messages
        : messages.slice(0, classificationEndIndex);
    const observedMessages = classificationMessages
      .filter(
        (msg): msg is IMessage & { text: string } =>
          typeof msg.text === 'string' && msg.text.trim().length > 0,
      )
      .map((msg) => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        text:
          msg.text.length <= 4000
            ? msg.text
            : `${msg.text.slice(0, 4000)}... [truncated]`,
      }))
      .slice(-100);
    const reportHtsCode = cleanHtsValue(HTSCode);
    const reportCmdtDesc = cleanHtsValue(cmdtDesc);
    const reportSummaryText = cleanHtsValue(finalSummaryText);
    const reportLegalBasisSummary = cleanHtsValue(finalLegalBasisSummary);
    const reportConfidenceNote = cleanHtsValue(finalConfidenceNote);
    const reportReasoningTrace = finalReasoningTrace
      .map(cleanHtsValue)
      .filter(Boolean)
      .slice(0, 20);
    const reportFactsReliedOn = finalFactsReliedOn
      .map(cleanHtsValue)
      .filter(Boolean)
      .slice(0, 20);
    const generatedLatest = {
      stage: 'FINAL_CLASSIFICATION',
      hts_code: reportHtsCode || null,
      cmdt_desc: reportCmdtDesc || null,
      summary_text: reportSummaryText || null,
      legal_basis_summary: reportLegalBasisSummary || null,
      confidence_note: reportConfidenceNote || null,
      reasoning_trace: reportReasoningTrace,
      facts_relied_on: reportFactsReliedOn,
      chapter_number: cleanHtsValue(finalChapterNumber) || null,
      chapter_description: cleanHtsValue(finalChapterDescription) || null,
    };
    const finalReport = reportHtsCode
      ? {
          version: 1 as const,
          source: 'publish_final_classification' as const,
          threadId: htsThreadIdRef.current,
          savedAt: new Date().toISOString(),
          payload: {
            thread_id: htsThreadIdRef.current,
            product_desc: cleanHtsValue(userProductDesc) || null,
            hts_code: reportHtsCode,
            cmdt_desc: reportCmdtDesc || null,
            summary_text: reportSummaryText || null,
            legal_basis_summary: reportLegalBasisSummary || null,
            confidence_note: reportConfidenceNote || null,
            reasoning_trace: reportReasoningTrace,
            facts_relied_on: reportFactsReliedOn,
            chapter_number: cleanHtsValue(finalChapterNumber) || null,
            chapter_description: cleanHtsValue(finalChapterDescription) || null,
          },
          generated: generatedLatest,
          streamItem: streamItems[streamItems.length - 1] || '',
        }
      : null;

    const fields: any = {
      htsChatHistory: {
        version: 1,
        source: 'hts-classification-managed-agent',
        threadId: htsThreadIdRef.current,
        savedAt: new Date().toISOString(),
        observed: { messages: observedMessages, streamItems: streamItems },
        chatkitThread: {
          available: false,
          reason:
            'Managed-agent threads do not have an external ChatKit transcript; using client-observed messages instead.',
        },
      },
    };

    if (finalReport) {
      fields.htsFinalReport = finalReport;
    }

    return fields;
  };

  const saveTicket = async (name: string) => {
    setSavingTicket(true);
    const followUpsForSave =
      followupItemsRef.current.length > 0
        ? followupItemsRef.current
        : followupItems;
    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const draftTicketName =
      savedString(name) ||
      savedString(ticketDraft?.ticketName) ||
      resumedTicketNameRef.current;

    const query: any = {
      owner: ownerQuery,
      ...(draftTicketName && { ticketName: draftTicketName }),
      lastUpdate: formatted_dt,
      docType: 'Import Unified',
      sharedWith: shareEmail,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      // userProductDescFix is only ever set by hydrateSavedImportTicket, so on
      // a fresh classification it stays '' — read the live description first
      // (same order EARChatUI uses) or the ticket saves with no product name.
      classificationValue: userProductDesc || userProductDescFix || cmdtDesc,
      followUps: followUpsForSave,
      aiStream: chatkitStreamData,
      exNotes: explanatoryNotes,
      chapterNotes: sectionNotes,
      crossRulings: crossLinks,
      commodity: {
        commodityName: cmdtDesc,
        harmonizedScheduleCode: HTSCode,
        material: material,
        intendedUse: intendedUse,
        physicalState: physicalState,
      },
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'created classification ticket',
        timeStamp: Date.now(),
      },
      ...buildHtsChatHistoryFields(),
    };
    handleTicket(query);
  };

  const saveDraftStage1 = async (name: string) => {
    setSavingTicket(true);
    const followUpsForSave =
      followupItemsRef.current.length > 0
        ? followupItemsRef.current
        : followupItems;
    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const draftTicketName =
      savedString(name) ||
      savedString(ticketDraft?.ticketName) ||
      savedString(newTicketName) ||
      resumedTicketNameRef.current;

    const query: any = {
      owner: ownerQuery,
      ...(draftTicketName && { ticketName: draftTicketName }),
      lastUpdate: formatted_dt,
      docType: 'Import Unified',
      sharedWith: shareEmail,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      // userProductDescFix is only ever set by hydrateSavedImportTicket, so on
      // a fresh classification it stays '' — read the live description first
      // (same order EARChatUI uses) or the ticket saves with no product name.
      classificationValue: userProductDesc || userProductDescFix || cmdtDesc,
      followUps: followUpsForSave,
      aiStream: chatkitStreamData,
      exNotes: explanatoryNotes,
      chapterNotes: sectionNotes,
      crossRulings: crossLinks,
      commodity: {
        commodityName: cmdtDesc,
        harmonizedScheduleCode: HTSCode,
        material: material,
        intendedUse: intendedUse,
        physicalState: physicalState,
      },
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'created classification ticket',
        timeStamp: Date.now(),
      },
      ...buildHtsChatHistoryFields(),
    };
    const new_case = {
      ...ticketDraft,
      groupId: cur_user.selected_team.id,
      ...query.basics,
      ...query,
    };

    setTicketDraft(new_case);
  };

  const createTicket = async (name: string) => {
    const new_case = {
      ...ticketDraft,
      ticketName: name,
      sharedWith: shareEmail,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });
    // setTicketDraft({});
    if (resp?.success) {
      setTicketID(resp.result._id);
      setIsSavedStageTwo(true);
      shareEmail.forEach((email) => {
        handleShareTicketNotification(
          ticketDraft.ticketName,
          cur_user,
          email,
          resp.result._id,
        );
      });
      setSaving(false);
      setModalOpen2(false);
      enqueueSnackbar('Successfully created new case', {
        variant: 'success',
      });
      completePendingLeaveAfterSave();
      // history.push({
      //   pathname: `/monitor/ticket/${resp.result._id}`,
      // });
    } else if (resp) {
      enqueueSnackbar(
        'Error saving new case. Uploaded file(s) may be too large.',
        {
          variant: 'error',
        },
      );
    }
  };

  const createTicket3 = async (name: string) => {
    const new_case = {
      ...ticketDraft,
      ticketName: name,
      frequency: frequency,
      sharedWith: shareEmail,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });
    // setTicketDraft({});
    if (resp?.success) {
      setTicketID(resp.result._id);
      setIsSavedStageThree(true);
      resetTicketDraft();
      shareEmail.forEach((email) => {
        handleShareTicketNotification(
          ticketDraft.ticketName,
          cur_user,
          email,
          resp.result._id,
        );
      });
      setSaving(false);
      setTicketNameOpen(false);
      setNewTicketName('');
      enqueueSnackbar('Successfully created new case', {
        variant: 'success',
      });
      completePendingLeaveAfterSave();
      // history.push({
      //   pathname: `/monitor/ticket/${resp.result._id}`,
      // });
    } else if (resp) {
      enqueueSnackbar(
        'Error saving new case. Uploaded file(s) may be too large.',
        {
          variant: 'error',
        },
      );
    }
  };

  const updateExistingTicketFromLeaveModal = async () => {
    setSaving(true);
    try {
      const ticketUpdate = getTicketDraftForUpdate();
      const resp = await CasesAPI.updateCase(ticketUpdate);

      if (resp?.success) {
        if (currentStage >= 3) {
          setIsSavedStageThree(true);
          resetTicketDraft();
        } else {
          setIsSavedStageTwo(true);
        }
        shareEmail.forEach((email) => {
          handleShareTicketNotification(
            ticketUpdate.ticketName,
            cur_user,
            email,
            resp.result._id,
          );
        });
        setSaving(false);
        setModalOpen2(false);
        setTicketNameOpen(false);
        setNewTicketName('');
        enqueueSnackbar('Successfully created new case', {
          variant: 'success',
        });
        completePendingLeaveAfterSave();
      } else {
        leaveSaveInProgressRef.current = false;
        setSaving(false);
        enqueueSnackbar(
          'Error saving new case. Uploaded file(s) may be too large.',
          {
            variant: 'error',
          },
        );
      }
    } catch (error) {
      leaveSaveInProgressRef.current = false;
      setSaving(false);
      enqueueSnackbar(
        'Error saving new case. Uploaded file(s) may be too large.',
        {
          variant: 'error',
        },
      );
    }
  };

  const handleSaveTicketFromLeaveModal = () => {
    if (!userHasMonitor) {
      return;
    }

    leaveSaveInProgressRef.current = true;
    setIsLeaveModalOpen(false);

    if (currentStage <= 1) {
      setModalOpen(true);
      return;
    }

    if (ticketDraft._id) {
      updateExistingTicketFromLeaveModal();
      return;
    }

    if (currentStage === 2) {
      setModalOpen2(true);
      return;
    }

    setTicketNameOpen(true);
  };

  const STAGE_STEP_LABELS: Record<number, string> = {
    1: 'HTS Classification',
    2: 'Tariffs & Duties',
    3: 'Customs Flags',
  };

  // Surfaces the same "can't skip a running step" feedback as the Export
  // Control workflow's Phase In Progress modal, instead of a silent no-op —
  // clicking a different step's card while one is running tells the user why
  // nothing happened rather than leaving the panel looking unresponsive.
  const blockStepSwitchWhileBusy = (targetStage: number) => {
    if (!isCurrentStageUnresolved || currentStage === targetStage) return false;
    setRunningStepLabel(STAGE_STEP_LABELS[currentStage] || 'The current step');
    setAttemptedStepLabel(STAGE_STEP_LABELS[targetStage] || 'that step');
    setIsStepRunningModalOpen(true);
    return true;
  };

  const handleStep1Click = () => {
    // Don't let the right-side panel jump the workflow into another stage
    // while the active one is still running.
    if (blockStepSwitchWhileBusy(1)) return;
    // Once the workflow has moved past stage 1 without ever completing
    // classification, that stage is skipped and locked — same rule as the
    // Tariffs & Duties stage in handleStep2Click.
    const isClassificationSkipped = currentStage > 1 && !HTSCode;
    if (HTSCode === '' && userHasMonitor && !isClassificationSkipped) {
      setCurrentStage(1);
      setMessages([]);
    }
  };

  const handleStep2Click = () => {
    // Don't let the right-side panel jump the workflow into another stage
    // while the active one is still running.
    if (blockStepSwitchWhileBusy(2)) return;
    // Mirrors handleStep1Click's own guard (HTSCode === '') — once this step
    // is complete, clicking its Classification Progress card shouldn't jump
    // the workflow back into it and re-push a fresh TARIFF_CAL bubble on top
    // of whatever's already showing. Once it's been skipped (workflow moved
    // past it without calculating), it's likewise locked — the user can't
    // reopen a stage that's already been left behind.
    const isTariffStepSkipped = currentStage > 2 && !tariffCalculated;
    if (userHasMonitor && !tariffCalculated && !isTariffStepSkipped) {
      setCurrentStage(2);
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            sender: 'bot',
            text: '',
            type: 'TARIFF_CAL',
          },
        ]);
      }, 400);
    }
  };

  const handleStep3Click = () => {
    // Don't let the right-side panel jump the workflow into another stage
    // while the active one is still running.
    if (blockStepSwitchWhileBusy(3)) return;
    const isCustomsScreeningComplete = messages.some(
      (message) => message.type === 'SCREEN_RESULT',
    );
    if (userHasMonitor && !isCustomsScreeningComplete) {
      setCurrentStage(3);
      if (HTSCode) {
        // Already classified — reuse that HTS code and skip straight to origin.
        setInputHTS(HTSCode);
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            sender: 'bot',
            text: 'Please provide enter country of origin?',
          },
        ]);
      } else {
        // Ask for HTS code
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            sender: 'bot',
            text: 'Please provide your HTS Code to begin the screening and compliance.',
          },
        ]);
      }
    }
  };

  // Add this helper function inside your ChatUI component, before the return statement
  const renderSaveButton = (stage: number, onClickHandler: () => void) => {
    if (ticketId && isSavedStageOne && stage < 2) {
      return (
        <Link to={`/monitor/ticket/${ticketId}`} className='option-button'>
          View Saved
        </Link>
      );
    } else if (ticketId && isSavedStageTwo && stage < 3) {
      return (
        <Link to={`/monitor/ticket/${ticketId}`} className='option-button'>
          View Saved
        </Link>
      );
    } else if (ticketId && isSavedStageThree && stage <= 3) {
      return (
        <Link to={`/monitor/ticket/${ticketId}`} className='option-button'>
          View Saved
        </Link>
      );
    }
    return (
      <Tooltip
        title={
          stage === 1
            ? 'Save progress or you can save all results at once at the end of the workflow'
            : 'Save all the results from this Workflow in one'
        }
        placement='bottom-start'
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              display: 'flex',
              padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              borderRadius: 'var(--radius-md, 8px)',
              background: 'var(--Background-200, var(--bg-colors))',
              color: 'var(--text-prim25',
              maxWidth: '700px',
              '& .MuiTooltip-arrow': {
                color: 'var(--Background-200, var(--bg-colors))',
              },
            },
          },
        }}
      >
        <button
          type='button'
          className='option-button'
          onClick={onClickHandler}
        >
          {stage === 3 ? 'Save Workflow' : 'Save'}
        </button>
      </Tooltip>
    );
  };

  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;
  const customsScreeningComplete = messages.some(
    (message) => message.type === 'SCREEN_RESULT',
  );
  // crossLinks come from live API state and could contain unsafe URLs (e.g.
  // javascript:). Only render anchors for http/https links — same filter
  // Classification.tsx uses.
  const safeCrossLinks = crossLinks.filter((link) => {
    try {
      const { protocol } = new URL(link.url);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  });
  // Same CROSS Rulings card design as Classification.tsx's live view
  // (bite-classify-cross-link-card: title + relevance % + note) instead of
  // the generic timeline renderer's plain joined-URL list.
  const crossRulingsDetailValue =
    safeCrossLinks.length > 0 ? (
      <div className='bite-classify-cross-links'>
        {safeCrossLinks.map((item, index) => (
          <div
            key={`${item.url}-${index}`}
            className='bite-classify-cross-link-card'
          >
            <a
              href={item.url}
              target='_blank'
              rel='noopener noreferrer'
              className='bite-classify-cross-link-title'
            >
              {item.title || item.url}
            </a>

            {typeof item.relevance_percent === 'number' && (
              <div className='bite-classify-cross-link-relevance'>
                Relevance: {item.relevance_percent}%
              </div>
            )}

            {item.relevance_note && (
              <div className='bite-classify-cross-link-note'>
                {item.relevance_note}
              </div>
            )}
          </div>
        ))}
      </div>
    ) : (
      <p className='bite-classify-path-awaiting'>Awaiting information...</p>
    );
  const importProgressSteps: WorkflowProgressStep[] = [
    {
      label: 'HTS Classification',
      detail: cmdtDesc || undefined,
      // Backend proposed a code but hasn't finalized it yet (FINAL_REVIEW) — surface
      // that on the right-side progress panel instead of just "In progress".
      value: reviewStage === 'FINAL_REVIEW' ? 'Under Review' : undefined,
      status: isHtsResultConfirmed
        ? 'complete'
        : hasImportStarted && currentStage === 1
        ? 'running'
        : currentStage > 1
        ? 'skipped'
        : 'pending',
      details: isHtsResultConfirmed
        ? [
            { label: 'HS Code', value: HTSCode },
            {
              label: 'Product Information',
              value: cmdtDesc,
            },
            {
              label: 'Section and Chapter Notes',
              value: sectionNotes,
            },
            {
              label: 'CROSS Rulings',
              value: crossRulingsDetailValue,
            },
          ]
        : undefined,
      onClick: handleStep1Click,
    },
    {
      label: 'Tariffs & Duties',
      hideDetail: tariffCalculated,
      detail:
        tariffInputs.originCountry || originCntry
          ? `Origin: ${tariffInputs.originCountry || originCntry}`
          : undefined,
      status: tariffCalculated
        ? 'complete'
        : currentStage === 2
        ? 'running'
        : currentStage > 2
        ? 'skipped'
        : 'pending',
      collapsedBody:
        tariffCalculated && hasTariffSnapshotResults(tariffData) ? (
          <ReadOnlyTariffMirror
            snapshot={tariffData}
            embedded
            embeddedView='summary'
          />
        ) : undefined,
      expandedBody:
        tariffCalculated && hasTariffSnapshotResults(tariffData) ? (
          <ReadOnlyTariffMirror snapshot={tariffData} embedded />
        ) : undefined,
      onClick: handleStep2Click,
    },
    {
      label: 'Customs Flags',
      hideDetail: customsScreeningComplete,
      detail: customsScreeningComplete
        ? 'Customs and compliance checks completed.'
        : undefined,
      status: customsScreeningComplete
        ? 'complete'
        : currentStage === 3
        ? 'running'
        : 'pending',
      details: customsScreeningComplete
        ? [
            {
              label: 'HTS Code',
              value: HTSCode || inputHTS || tariffInputs.htsCode || userECCN,
            },
            {
              label: 'Origin Country',
              value: tariffInputs.originCountry || originCntry,
            },
            { label: 'Screening Status', value: 'Checks completed' },
          ]
        : undefined,
      collapsedBody: customsScreeningComplete ? (
        <CustomsFlagsSummary
          hsCode={HTSCode || inputHTS || tariffInputs.htsCode || userECCN}
          aiSums={ticketDraft?.aiSums}
        />
      ) : undefined,
      expandedBody: customsScreeningComplete ? (
        <CustomsFlagsDetails
          hsCode={HTSCode || inputHTS || tariffInputs.htsCode || userECCN}
          aiSums={ticketDraft?.aiSums}
          workflowResults={ticketDraft?.results}
        />
      ) : undefined,
      onClick: handleStep3Click,
    },
  ];

  return (
    <>
      <style>
        {`
          @keyframes chatStreamingDot {
            0%, 20% {
              opacity: 0.2;
              transform: translateY(0);
            }
            50% {
              opacity: 1;
              transform: translateY(-1px);
            }
            100% {
              opacity: 0.2;
              transform: translateY(0);
            }
          }

          .streaming-dot {
            display: inline-block;
            min-width: 0.2em;
            line-height: 1;
            vertical-align: baseline;
            animation: chatStreamingDot 1s ease-in-out infinite;
          }
        `}
      </style>
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
        <WorkflowPageHeader
          title='Import Workflow'
          extraActions={<CreditMeterButton packType='credits' size='sm' />}
          description='Classify products, calculate tariffs and review customs requirements in one guided workflow.'
          newActionLabel='New Import'
          onNew={() => resetWorkflowState(true)}
          onSave={() => setModalOpen(true)}
          disableNew={
            (isMessageInputDisabled && userHasMonitor) || trialLimitReached
          }
          savedPath={
            isWorkflowSavedForCurrentStage && (ticketId || ticketDraft?._id)
              ? `/monitor/ticket/${ticketId || ticketDraft?._id}`
              : undefined
          }
          disableSave={
            !userHasMonitor ||
            !hasUnsavedWorkflowWork ||
            savingTicket ||
            trialLimitReached
          }
        />
      <WorkflowNotice>
        <p>
          The HTS classification, tariff and duty simulation, and customs flag
          check are provided for general, educational and planning purposes
          only—not for filing and entry. Users should verify product scope,
          effective dates, exemptions and trade programs, and consult a licensed
          customs broker where required.
        </p>
      </WorkflowNotice>
      {insufficientCredits && messages.length === 0 && (
        <WorkflowCreditWarning
          remaining={remainingCredits}
          cost={WORKFLOW_CREDIT_COST}
        />
      )}
      <div
        ref={workflowGridRef}
        className='standard-workflow-grid chat-workflow-row import-workflow-grid'
      >
        <div className='standard-workflow-assistant-card bill-container'>
          <WorkflowAssistantHeader
            title='Import Assistant'
            status={
              trialLimitReached
                ? 'unavailable'
                : isMessageInputDisabled && userHasMonitor
                ? 'running'
                : 'idle'
            }
          />
          {!userHasMonitor && (
            <RequiredLicense
              userLicense={normalizedLicense}
              minimumLicense='business'
            />
          )}
          {trialLimitReached ? (
            <TrialRunUsedPanel workflowName='Import Workflow' />
          ) : messages.length > 0 ? (
            <div className='chat-container'>
              <div
                ref={chatScrollRef}
                className='chat-body standard-workflow-chat-scroll standard-workflow-conversation'
              >
                {messages.map((msg, msgIndex) => (
                  <div
                    key={msg.id}
                    className={`chat-message ${
                      msg.sender === 'user' ? 'user' : 'bot'
                    }`}
                  >
                    {msg.sender === 'bot' && (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='32'
                        height='32'
                        viewBox='0 0 32 32'
                        fill='none'
                      >
                        <g clipPath='url(#clip0_1_40254)'>
                          <path
                            d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z'
                            fill='#4574EE'
                          />
                          <path
                            d='M17.5915 13.6496C16.9135 14.4731 15.4893 14.9301 14.5015 14.4289C13.3459 13.8427 13.0921 11.9548 13.5386 10.8594C14.1072 9.47015 16.0287 8.59755 17.2625 9.68387C18.3252 10.6166 18.488 12.5592 17.5897 13.6486L17.5915 13.6496Z'
                            fill='#23377D'
                          />
                          <path
                            d='M15.6677 10.1001C15.3016 7.89481 14.9878 5.68156 14.7297 3.46243C14.9183 3.46175 15.1052 3.46004 15.2948 3.45758C16.0181 5.34973 16.5393 7.37302 17.0281 9.33704C16.8184 9.6877 16.6096 10.0412 16.4 10.3918C16.1168 10.3046 15.8828 10.1981 15.6688 10.0983L15.6677 10.1001Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M15.9794 5.46298C16.0854 5.80766 16.1883 6.1577 16.2877 6.50568C15.9017 6.45197 15.5028 6.47421 15.1297 6.57564C15.0774 6.19999 15.0286 5.82639 14.979 5.44998C15.3047 5.4141 15.6466 5.4328 15.9812 5.46401L15.9794 5.46298Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M15.5266 10.5469C15.5252 10.5247 15.5227 10.5042 15.5185 10.4827C15.6634 10.533 15.8015 10.6032 15.9283 10.6931C16.6211 11.1812 16.7127 12.0131 16.4608 12.7753C16.3127 13.2258 15.9697 13.6424 15.5542 13.805C15.4665 12.7229 15.5662 11.6299 15.5277 10.5451L15.5266 10.5469Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M14.3623 10.623C14.4416 10.564 14.5248 10.5191 14.6119 10.4837C14.7086 10.4442 14.8098 10.4216 14.9122 10.4093C14.8844 10.4575 14.8687 10.5176 14.872 10.5862C14.9116 11.6691 14.8122 12.7576 14.8973 13.8358C14.782 13.8002 14.6625 13.7431 14.5474 13.6577C14.3375 13.5055 14.1861 13.2847 14.0707 13.0513C13.7178 12.3377 13.6457 11.1503 14.3615 10.6202L14.3623 10.623Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M16.9285 12.3923C16.8894 12.5675 16.9025 12.7799 16.9381 12.9577C17.3502 13.1146 17.8135 13.2987 18.2256 13.4557C18.4536 13.1227 18.5659 12.7135 18.541 12.3109C18.063 12.2969 17.4059 12.3868 16.9275 12.3941L16.9285 12.3923Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M17.3389 11.4576C17.3616 11.5875 17.4327 11.7285 17.5075 11.8384C17.8308 11.8273 18.1973 11.8198 18.5206 11.8087C18.5787 11.5183 18.5398 11.21 18.4079 10.9432C18.0805 11.0687 17.6607 11.317 17.3407 11.4586L17.3389 11.4576Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M8.12142 14.8218C6.99677 18.6559 8.45308 22.4149 12.132 24.103C17.2109 26.4326 25.1027 24.9262 26.6314 19.534C26.8551 18.746 26.9039 17.8403 26.6117 17.0999C26.493 16.7979 26.3189 16.5259 26.0793 16.297C25.3812 15.6367 24.3409 15.5482 23.3983 15.3733C22.5909 15.224 21.7738 14.9761 21.1406 14.4509C20.7407 14.12 20.4334 13.6948 20.0867 13.3089C18.5567 11.6132 16.2637 10.0916 13.9694 9.7651C12.1334 9.50314 10.6679 10.4318 9.62315 11.7916C8.93539 12.6856 8.42955 13.7681 8.11963 14.8208L8.12142 14.8218Z'
                            fill='white'
                          />
                          <path
                            d='M8.28084 14.4393C7.1562 18.2733 8.6125 22.0324 12.2914 23.7204C17.3704 26.0501 25.2621 24.5436 26.7908 19.1514C27.0146 18.3635 27.0633 17.4578 26.7712 16.7174C26.668 17.3129 26.5826 17.8116 26.498 18.02C25.9471 19.3623 24.9208 20.5207 23.657 21.2323C21.4592 22.4719 18.7318 22.4909 16.3099 21.7787C13.6909 21.0075 11.2184 19.2894 10.1836 16.7647C9.494 15.0826 9.4962 13.2257 9.78257 11.409C9.09481 12.3031 8.58897 13.3856 8.27905 14.4383L8.28084 14.4393Z'
                            fill='#EDEDED'
                          />
                          <path
                            d='M26.1648 19.4883C25.1974 19.6492 24.0073 17.2326 24.2273 16.4306C24.3087 16.1369 24.6346 15.9892 24.9344 15.9241C26.0395 15.6855 27.1937 16.0016 27.2202 17.2723C27.2317 17.9127 27.0187 18.583 26.6642 19.1144C26.5119 19.341 26.3423 19.4574 26.1658 19.4865L26.1648 19.4883Z'
                            fill='#23377D'
                          />
                          <path
                            d='M10.2253 16.7191C9.72592 17.6625 8.42206 18.3938 7.35249 18.1003C6.10476 17.7563 5.47582 15.9567 5.69509 14.7969C5.97485 13.3218 7.68172 12.0823 9.10912 12.8992C10.3381 13.6017 10.8844 15.4702 10.2225 16.7199L10.2253 16.7191Z'
                            fill='#23377D'
                          />
                          <path
                            d='M7.63117 13.6256C6.83174 11.5394 6.08257 9.43214 5.38516 7.30954C5.5696 7.27071 5.75405 7.23188 5.9385 7.19306C7.02494 8.90184 7.93942 10.7805 8.81163 12.6086C8.6766 12.9952 8.54158 13.3818 8.40655 13.7683C8.11055 13.7404 7.86075 13.6819 7.63117 13.6256Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M7.0096 9.01929C7.1838 9.33664 7.35311 9.65832 7.51781 9.97973C7.12817 10.0025 6.74202 10.1059 6.39704 10.2784C6.27139 9.92236 6.1478 9.56276 6.02497 9.20597C6.33508 9.1063 6.67647 9.05565 7.0096 9.01929Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M7.58337 14.0935C7.57733 14.071 7.57204 14.0512 7.5629 14.034C7.71509 14.0552 7.86515 14.0966 8.00564 14.1586C8.78119 14.4992 9.03674 15.2947 8.94266 16.0932C8.8866 16.5659 8.63448 17.0397 8.26026 17.2834C7.95947 16.2402 7.83804 15.1505 7.58234 14.0953L7.58337 14.0935Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M6.4554 14.4C6.52176 14.3264 6.59292 14.265 6.67274 14.2134C6.76026 14.1568 6.85438 14.1134 6.95152 14.0814C6.9326 14.1348 6.9308 14.1957 6.94715 14.2623C7.20209 15.3147 7.32277 16.4016 7.62281 17.4419C7.50052 17.4309 7.37397 17.3983 7.24447 17.3379C7.00871 17.2303 6.81551 17.0449 6.65773 16.8395C6.17034 16.2103 5.86105 15.0622 6.45822 14.3993L6.4554 14.4Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M9.3215 15.6213C9.31713 15.8022 9.37511 16.0072 9.44374 16.1731C9.87853 16.2454 10.3698 16.3313 10.8046 16.4037C10.9618 16.0323 10.99 15.6079 10.8858 15.2189C10.415 15.2997 9.78801 15.519 9.3215 15.6213Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M9.53921 14.6222C9.58731 14.7453 9.68531 14.8685 9.7796 14.9611C10.0947 14.8858 10.4508 14.8055 10.766 14.7302C10.7652 14.4343 10.6646 14.138 10.4827 13.9043C10.1875 14.0936 9.82495 14.4203 9.53921 14.6222Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M11.7836 16.1759C12.1634 17.4577 13.3539 18.4952 14.687 18.5716C15.7023 18.629 16.6735 18.1725 17.5478 17.6529C18.422 17.1333 19.2697 16.5316 20.2461 16.252C20.7607 16.1036 21.9847 16.1171 22.3216 15.6327C22.6637 15.1393 22.298 14.3613 21.9624 13.853C21.9039 13.7645 21.8477 13.6844 21.7955 13.6138C21.2395 12.8806 20.5831 12.2181 19.8236 11.7011C18.7473 10.9653 17.4504 10.5 16.1353 10.6484C15.9947 10.6649 15.8521 10.685 15.7117 10.7135C14.5354 10.9349 13.4173 11.5138 12.647 12.4269C11.7834 13.4482 11.4027 14.8959 11.7825 16.1777L11.7836 16.1759Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M11.7829 16.176C12.1628 17.4578 13.3532 18.4953 14.6863 18.5717C15.7016 18.629 16.6729 18.1726 17.5471 17.653C18.4213 17.1334 19.2691 16.5317 20.2454 16.2521C20.76 16.1037 21.9841 16.1172 22.321 15.6328C22.663 15.1394 22.2973 14.3613 21.9617 13.8531C20.8457 13.933 19.3982 14.7315 18.7375 15.1743C17.5624 15.9633 16.3055 16.8361 14.8901 16.7907C14.1971 16.7694 13.4728 16.4846 13.0945 15.9017C12.6501 15.2187 12.7805 14.3078 13.0927 13.5566C13.5738 12.3931 14.4606 11.4019 15.563 10.7925C15.6117 10.7658 15.6605 10.7392 15.7082 10.7143C14.532 10.9357 13.4138 11.5146 12.6435 12.4278C11.7799 13.449 11.3992 14.8967 11.779 16.1785L11.7829 16.176Z'
                            fill='#4488E1'
                          />
                          <path
                            d='M15.2769 13.9217C15.8267 14.5417 15.7991 16.1909 14.9186 15.673C14.5204 15.4383 14.4312 14.9199 14.3535 14.5058C14.2654 14.0309 14.5404 13.4845 15.0687 13.7539C15.1459 13.7936 15.2155 13.8505 15.2779 13.9199L15.2769 13.9217Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M19.8962 12.4533C20.3787 12.7319 20.712 13.789 20.041 13.647C19.737 13.5834 19.5699 13.2702 19.433 13.0244C19.2753 12.7404 19.3357 12.3346 19.7288 12.3924C19.7856 12.4014 19.8426 12.4224 19.898 12.4543L19.8962 12.4533Z'
                            fill='#4CC9F0'
                          />
                        </g>
                        <defs>
                          <clipPath id='clip0_1_40254'>
                            <path
                              d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z'
                              fill='white'
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    )}

                    {msg.type === 'CLASSIFICATION_RESULT' &&
                    HTSCode &&
                    cmdtDesc &&
                    explanatoryNotes &&
                    sectionNotes ? (
                      <>
                        <div
                          className='message-bubble'
                          style={{ maxWidth: '90%' }}
                        >
                          <ClassifyResultsComp
                            name='HS Code:'
                            hts_code={HTSCode}
                            cmdt_desc={cmdtDesc}
                            explanatory_notes={explanatoryNotes}
                            section_chapter_notes={sectionNotes}
                            cross_data_links={safeCrossLinks}
                            isExpands
                            saveBtn={() => {
                              setModalOpen(true);
                            }}
                            handleRowClick={() => {
                              return;
                            }}
                          />

                          <p style={{ margin: '5px 0' }}>
                            What would you like to do next?
                          </p>

                          <ol className='options-container workflow-control-options'>
                            <li>
                              <button
                                type='button'
                                className='option-button'
                                onClick={() =>
                                  handleOptionClick('Calculate Tariffs')
                                }
                              >
                                Calculate Tariffs
                              </button>
                            </li>
                            <li>
                              {renderSaveButton(1, () =>
                                handleOptionClick('Save'),
                              )}
                            </li>
                          </ol>
                        </div>
                      </>
                    ) : msg.type === 'TARIFF_RESULT' ? (
                      <div
                        className='message-bubble'
                        style={{ maxWidth: '90%' }}
                      >
                        <div
                          style={{
                            color: '#000',
                            fontSize: '14px',
                            lineHeight: '1.4',
                            width: '100%',
                          }}
                        >
                          <TariffPage
                            primary={HTSCode || tariffInputs.htsCode || ''}
                            country={
                              tariffInputs.originCountry || originCntry || ''
                            }
                            onCalculationDone={handleTariffCalculationDone}
                            initialResults={
                              isResumedTicket &&
                              hasTariffSnapshotResults(tariffData)
                                ? tariffData
                                : undefined
                            }
                            disableAutoFetch={
                              isResumedTicket &&
                              hasTariffSnapshotResults(tariffData)
                            }
                            productVal={productValue}
                            effectDates={effectDate}
                            loadDates={loadDate}
                            inputs={tariffInputs}
                            loadingCountryValue={loadingCountry}
                            amountInputsValue={amountInputs}
                            materialPctByKeyValue={materialPctByKey}
                            setEnabledExclusionsValue={setEnabledExclusions}
                            setEnabledFtasValue={setEnabledFtas}
                            isExpands={true}
                            setLineSplitShow={setLineSplitShow}
                            setLoadingCountryShow={setLoadingCountryShow}
                            setAmmountShow={setAmmountShow}
                            setCountryFieldKey={setCountryFieldKey}
                            setCountryFieldCodes={setCountryFieldCodes}
                          />
                        </div>
                        {tariffCalculated && (
                          <>
                            <p style={{ margin: '5px 0' }}>
                              What would you like to do next?
                            </p>

                            <ol className='options-container workflow-control-options'>
                              <li>
                                <button
                                  type='button'
                                  className='option-button'
                                  onClick={() =>
                                    handleOptionClick('Proceed to Screening')
                                  }
                                >
                                  Proceed to Screening
                                </button>
                              </li>
                              <li>
                                {renderSaveButton(2, () =>
                                  handleOptionClick('Save'),
                                )}
                              </li>
                            </ol>
                          </>
                        )}
                      </div>
                    ) : msg.type === 'SCREEN_RESULT' ? (
                      <div
                        className='message-bubble standard-workflow-screening-message'
                      >
                        <ImportWorkflowScreening
                          primary={HTSCode || inputHTS || ''}
                          country={
                            tariffInputs.originCountry || originCntry || ''
                          }
                          isSelected={false}
                          isExpands={true}
                          onTrialLimitReached={() => setTrialLimitReached(true)}
                        />
                        <p style={{ marginBottom: '5px', marginTop: '5px' }}>
                          {'What would you like to do next?'}
                        </p>

                        <ol className='options-container workflow-control-options'>
                          <li>
                            {renderSaveButton(3, () =>
                              handleOptionClick('Save Workflow'),
                            )}
                          </li>
                          <li>
                            <button
                              type='button'
                              className='option-button'
                              onClick={() => handleOptionClick('New Session')}
                            >
                              New Session
                            </button>
                          </li>
                        </ol>
                      </div>
                    ) : msg.type === 'TARIFF_CAL' ? (
                      <div
                        className='message-bubble'
                        style={{ maxWidth: '90%' }}
                      >
                        <NewTariffRagSearch
                          userHasSearch={true}
                          inputs={tariffInputs}
                          setInputs={setTariffInputs}
                          effectDate={effectDate}
                          setEffectDate={setEffectDate}
                          loadDate={loadDate}
                          setLoadDate={setLoadDate}
                          productValue={productValue}
                          setProductValue={setProductValue}
                          lineSplitShow={lineSplitShow}
                          loadingCountryShow={loadingCountryShow}
                          ammountShow={ammountShow}
                          tariffResults={tariffData}
                          countryFieldKey={countryFieldKey}
                          loadingCountry={loadingCountry}
                          setLoadingCountry={setLoadingCountry}
                          countryFieldCodes={countryFieldCodes}
                          amountInputs={amountInputs}
                          setAmountInputs={setAmountInputs}
                          materialPctByKey={materialPctByKey}
                          setMaterialPctByKey={setMaterialPctByKey}
                          readOnly={false}
                          isBulkMode={false}
                        />

                        {!runCheck && (
                          <ol className='options-container workflow-control-options'>
                            <li>
                              <button
                                type='button'
                                className='option-button'
                                onClick={() => handleOptionClick('Run Check')}
                              >
                                Run Check
                              </button>
                            </li>
                          </ol>
                        )}
                      </div>
                    ) : msg.type === 'HTS_TEXT' ? (
                      <div
                        className='bite-chatkit-message bite-chatkit-message-assistant'
                        style={{ maxWidth: '90%', fontFamily: HTS_FONT_FAMILY }}
                      >
                        {msg.payload?.thoughtSteps && (
                          <HtsThoughtSummary
                            seconds={msg.payload.thoughtSeconds ?? 0}
                            steps={msg.payload.thoughtSteps}
                            isLive={
                              htsThinking &&
                              msg.id === htsResponseIdRef.current
                            }
                            startedAt={htsThinkingStartedAtRef.current}
                          />
                        )}
                        {/* Interim narration no longer becomes a message at all
                            (it lives only in the trail above), so this is always
                            the turn's real answer — no duplicate-suppression or
                            soft "note" styling needed any more. An answer-less
                            trail message (narration arrived before the answer)
                            simply has empty text and renders nothing. */}
                        {Boolean(msg.text) && (
                          <div className='bite-chatkit-message-text'>
                            <HtsMarkdownMessage
                              text={String(msg.text)}
                              animate={Boolean(msg.payload?.shouldStream)}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`message-bubble ${
                          msg.text === 'Thinking...' ? 'thinking' : ''
                        }`}
                      >
                        {msg.sender === 'user' &&
                          Array.isArray(msg.payload?.attachedFilenames) && (
                            <WorkflowAttachmentChips
                              names={msg.payload.attachedFilenames}
                              testId='import-message-attachment'
                            />
                          )}
                        <p
                          style={{
                            marginBottom: '0px',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          <WorkflowStreamingResponse
                            text={msg.text}
                            animate={
                              msg.sender === 'bot' &&
                              msgIndex === messages.length - 1
                            }
                          />
                        </p>
                        {/* 👇 Only show options if message is from bot and has options */}
                        {msg.sender === 'bot' &&
                          Array.isArray(msg.options) &&
                          msg.options?.length > 0 && (
                            <ol className='options-container workflow-control-options'>
                              {msg.options.map((option, index) => (
                                <li key={`${option}-${index}`}>
                                  <button
                                    type='button'
                                    className='option-button'
                                    onClick={() => handleOptionClick(option)}
                                  >
                                    {option}
                                  </button>
                                </li>
                              ))}
                            </ol>
                          )}
                      </div>
                    )}

                    {msg.sender === 'user' && (
                      <div className='profile-icon'>
                        {cur_user.basics?.firstName[0]}
                        {cur_user.basics?.lastName[0]}
                      </div>
                    )}
                  </div>
                ))}

                {htsThinking &&
                  currentStage === 1 &&
                  // Once the current turn's own bubble has real narration,
                  // ITS "Thought" panel (now live-updating, see
                  // attachHtsThoughtSummary/isLive above) already serves as
                  // the "still working" signal — showing this separate
                  // canned-text card too would be a second, redundant
                  // (and, unlike the real panel, fake) indicator. Only shown
                  // for the brief window before the first real interim note
                  // has streamed in.
                  !messages.some(
                    (m) =>
                      m.id === htsResponseIdRef.current &&
                      (m.payload?.thoughtSteps?.length ?? 0) > 0,
                  ) && (
                    <div className='chat-message bot'>
                      <HtsThinkingIndicator
                        startedAt={
                          htsThinkingStartedAtRef.current || Date.now()
                        }
                      />
                    </div>
                  )}
              </div>

              <WorkflowComposer
                inputRef={composerInputRef}
                value={input}
                onChange={setInput}
                onSubmit={handleSend}
                placeholder='Describe your product, its composition and intended use...'
                disabled={isMessageInputDisabled || attachments.isUploading}
                loading={isStageBusy || attachments.isUploading}
                attachments={composerAttachments}
              />
            </div>
          ) : (
            <div
              className='standard-workflow-empty-chat'
              style={{
                backgroundColor: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                minHeight: 'calc(100vh - 114px)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                fontFamily: 'sans-serif',
                width: '100%',
                padding: '24px 0',
                boxSizing: 'border-box',
                overflowY: 'auto',
                gap: '12px',
              }}
            >
              <WorkflowComposer
                inputRef={composerInputRef}
                value={input}
                onChange={setInput}
                onSubmit={handleSend}
                placeholder='Describe your product, its composition and intended use...'
                disabled={
                  isMessageInputDisabled ||
                  insufficientCredits ||
                  attachments.isUploading
                }
                loading={attachments.isUploading}
                attachments={composerAttachments}
              />
              {/* --- PHASE 2: OPTIONAL FIELDS --- */}
              {/* <div
                style={{ marginTop: '10px', paddingTop: '10px', width: '90%' }}
              >
                <div className='classify-header' style={{ opacity: 0.8 }}>
                  Optional Details (Improves Accuracy)
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    gap: '10px',
                    marginTop: '10px',
                  }}
                >
                  <div style={{ width: 'inherit' }}>
                    <label
                      style={{
                        fontSize: '12px',
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: '600',
                        fontFamily: 'Inter',
                      }}
                    >
                      Material
                    </label>
                    <input
                      className='classify-cont-sec-input'
                      style={{
                        padding: '8px',
                        background: 'var(--bg-colors)',
                      }}
                      placeholder='e.g. Steel, Cotton'
                      value={material}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault(); // Prevent new line
                          handleSend(e);
                        }
                      }}
                      onChange={(e) => setMaterial(e.target.value)}
                      disabled={!userHasMonitor}
                    />
                  </div>
                  <div style={{ width: 'inherit' }}>
                    <label
                      style={{
                        fontSize: '12px',
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: '600',
                        fontFamily: 'Inter',
                      }}
                    >
                      Intended Use
                    </label>
                    <input
                      className='classify-cont-sec-input'
                      style={{
                        padding: '8px',
                        background: 'var(--bg-colors)',
                      }}
                      placeholder='e.g. Medical, Filtration'
                      value={intendedUse}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault(); // Prevent new line
                          handleSend(e);
                        }
                      }}
                      onChange={(e) => setIntendedUse(e.target.value)}
                      disabled={!userHasMonitor}
                    />
                  </div>
                  <div style={{ width: 'inherit' }}>
                    <label
                      style={{
                        fontSize: '12px',
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: '600',
                        fontFamily: 'Inter',
                      }}
                    >
                      Physical State
                    </label>
                    <input
                      className='classify-cont-sec-input'
                      style={{
                        padding: '8px',
                        background: 'var(--bg-colors)',
                      }}
                      placeholder='e.g. Part, Finished, Set'
                      value={physicalState}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault(); // Prevent new line
                          handleSend(e);
                        }
                      }}
                      onChange={(e) => setPhysicalState(e.target.value)}
                      disabled={!userHasMonitor}
                    />
                  </div>
                </div>
              </div> */}
            </div>
          )}
        </div>
        <div
          className='standard-workflow-progress-card bill-container-fit-screen'
          style={trialLimitReached ? { opacity: 0.55 } : undefined}
        >
          <WorkflowProgressHeader
            title='Classification Progress'
            badge='Beta'
          />
          <WorkflowProgressTimeline
            steps={importProgressSteps}
            disableExpand
          />
        </div>
      </div>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <CreateTicketWizard
          cur_user={cur_user}
          handleSaveTicket={saveTicket}
          newTicketName={newTicketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={savingTicket}
        />
      </Modal>

      <Modal open={isModalOpen2} onClose={handleCloseModal2}>
        <CreateTicketWizard
          cur_user={cur_user}
          ticketType='tariff'
          setFrequency={setFrequency}
          frequency={frequency}
          dateStrategy={dateStrategy}
          setDateStrategy={setDateStrategy}
          handleSaveTicket={createTicket}
          newTicketName={newTicketName || ticketDraft.ticketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={saving}
        />
      </Modal>

      <Modal open={ticketNameOpen} onClose={handleTicketNameClose}>
        <CreateTicketWizard
          cur_user={cur_user}
          ticketType={'tariff'}
          setFrequency={setFrequency}
          frequency={frequency}
          dateStrategy={dateStrategy}
          setDateStrategy={setDateStrategy}
          handleSaveTicket={createTicket3}
          newTicketName={newTicketName || ticketDraft.ticketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={saving || false}
        />
      </Modal>

      <Dialog
        open={isStepRunningModalOpen}
        onClose={() => setIsStepRunningModalOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '520px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Step In Progress
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => setIsStepRunningModalOpen(false)}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              {runningStepLabel} is currently running. Please wait until it
              completes before switching to {attemptedStepLabel}.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-automate'
                onClick={() => setIsStepRunningModalOpen(false)}
              >
                <div>OK</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        className='dialog_box'
        open={isLeaveModalOpen}
        onClose={() => {
          setIsLeaveModalOpen(false);
          setPendingLocation(null);
        }}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Leave Import Workflow Tool?
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => {
              setIsLeaveModalOpen(false);
              setPendingLocation(null);
            }}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Leaving this workflow will discard your current work. Use Save
              Ticket to store this transaction in Saved Work before leaving.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={handleLeaveWithoutSaving}
              >
                <div>Leave Without Saving</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleSaveTicketFromLeaveModal}
              >
                <div>Save Ticket</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleLeaveAndOptOut}
              >
                <div>Leave + Don&apos;t Show Again</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatUI;
